import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MDBSpinner } from "mdb-react-ui-kit";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { api } from "../../services";
import { getLocalizedPlatformName } from "../../utils/platformNames";
import {
  handlePlatformLogoError,
  resolveIntegrationLogo,
  usesWideIntegrationLogo,
} from "../../utils/platformLogos";
import { usePermission } from "../../hooks/usePermission";
import { PERMISSION_ACTIONS, RBAC_MODULES } from "../../config/routePermissions";
import riyalIcon from "./assets/riyal.svg";
import {
  LIVE_SUBSCRIPTION,
  appId,
  connectPath,
  branchLimitForConnect,
  connectedBranchCount,
  connectionsForApp,
  featureKeyForApp,
  entitlementQtyForApp,
  formatDate,
  formatMinor,
  formatPeriodStartLabel,
  globalPlans,
  heldCoveringPlan,
  heldGlobalPlan,
  heldLaunchPlan,
  isGlobalCatalogPlan,
  isLaunchPlan,
  mergeBundleItems,
  planForApp,
  resolvedCoveringBranchLimit,
  sectionOf,
} from "./subscriptionCatalog";
import "./SubscriptionFlow.css";

const CHECKOUT_FLAG = "oh.subscription.checkout";
const MODEL_SWITCH_NOTICE_DEFAULT =
  "On {{date}}, your subscription changes to {{name}}. Your current services and branch limits stay the same until then, and you are not charged today for this switch.";
const BILLING_INTERVALS = new Set(["day", "week", "month", "year"]);

function billingInterval(plan) {
  const value = String(plan?.interval || "month").toLowerCase();
  if (value === "annual") return "year";
  return BILLING_INTERVALS.has(value) ? value : "month";
}
const SECTIONS = [
  { id: "addon", title: "loopsServices", hint: "loopsHint", pill: "byLoops", pillClass: "new" },
  { id: "integration", title: "delivery", hint: "deliveryHint", pill: "integrations", pillClass: "" },
  { id: "pos", title: "pos", hint: "posHint", pill: "posPill", pillClass: "teal" },
];

function Money({ value, language }) {
  return (
    <span className="sf-money" dir="ltr">
      <img src={riyalIcon} alt="" width={12} height={14} />
      {formatMinor(value, language)}
    </span>
  );
}

function QtyChange({ from, to }) {
  return <bdi className="sf-qty-change" dir="ltr">{`${from} → ${to}`}</bdi>;
}

function Logo({ app, name, compact = false }) {
  const logo = resolveIntegrationLogo(app) || app?.logo || "";
  const wide = usesWideIntegrationLogo(app);
  return (
    <span className={`sf-logo${wide ? " is-wide" : ""}${compact ? " is-row" : ""}`}>
      {logo ? (
        <img src={logo} alt="" onError={(event) => handlePlatformLogoError(event, app)} />
      ) : (
        <span>{String(name || "?").slice(0, 1)}</span>
      )}
    </span>
  );
}

function SubscriptionPage() {
  const pageRef = useRef(null);
  const { t, i18n } = useTranslation("translation");
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { can } = usePermission();
  const canPerform = can(RBAC_MODULES.SUBSCRIPTIONS, PERMISSION_ACTIONS.PERFORM_SUBSCRIPTION);
  const canCancel = can(RBAC_MODULES.SUBSCRIPTIONS, PERMISSION_ACTIONS.CANCEL_SUBSCRIPTION);
  const ts = useCallback((key, opts) => t(`subscriptionFlow.${key}`, opts), [t]);
  const contactUs = () => {
    window.location.href = "mailto:support@loops.sa";
  };

  useLayoutEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;
    const placeBar = () => {
      const rect = page.getBoundingClientRect();
      page.style.setProperty("--sf-bar-x", `${rect.left}px`);
      page.style.setProperty("--sf-bar-width", `${rect.width}px`);
    };
    placeBar();
    const observer = new ResizeObserver(placeBar);
    observer.observe(page);
    window.addEventListener("resize", placeBar);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", placeBar);
    };
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [plansError, setPlansError] = useState("");
  const [apps, setApps] = useState([]);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [connections, setConnections] = useState([]);
  const [renewsAt, setRenewsAt] = useState(null);
  const [mode, setMode] = useState("global");
  const [tierCode, setTierCode] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [baseline, setBaseline] = useState({});
  const [step, setStep] = useState("choose");
  const [agreed, setAgreed] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteError, setQuoteError] = useState("");
  const [busy, setBusy] = useState(false);
  const [flags, setFlags] = useState({});
  const [declined, setDeclined] = useState(false);
  const [focusId, setFocusId] = useState(null);
  const [globalOpen, setGlobalOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [pendingExpanded, setPendingExpanded] = useState(false);
  const [undoAskOpen, setUndoAskOpen] = useState(false);
  const [switchToCustom, setSwitchToCustom] = useState(false);
  const [modelSwitchConfirmOpen, setModelSwitchConfirmOpen] = useState(false);
  const seeded = useRef(false);
  const checkoutSeeded = useRef(false);
  const stagedFromQuery = useRef(false);

  const load = useCallback(async () => {
    const token = localStorage.getItem("token");
    const [appsRes, bundleRes, connectionRes] = await Promise.all([
      api.get("/available-apps/categorized", { headers: { Authorization: `Bearer ${token}` } }),
      api.get("/subscriptions/bundle").catch((err) => {
        setPlansError(err.response?.data?.message || ts("plansUnavailable"));
        return { data: { plans: [], subscription: null } };
      }),
      api.get("/subscriptions/bundle/connections").catch(() => ({ data: { connections: [], renewsAt: null } })),
    ]);
    const visible = (app) => app?.status !== "Pending" && !app?.isHidden;
    const seenAppIds = new Set();
    const catalog = [
      ...(appsRes.data?.categories?.addOns?.apps || []),
      ...(appsRes.data?.categories?.integrations?.apps || []),
    ].filter((app) => {
      if (!visible(app)) return false;
      const id = appId(app);
      if (!id || seenAppIds.has(id)) return false;
      seenAppIds.add(id);
      return true;
    });
    const nextSubscription = bundleRes.data?.subscription || null;
    setApps(catalog);
    setPlans(bundleRes.data?.plans || []);
    setSubscription(nextSubscription);
    setConnections(connectionRes.data?.connections || []);
    setRenewsAt(connectionRes.data?.renewsAt || nextSubscription?.renewsAt || null);
    if (bundleRes.data?.plansUnavailable) {
      setPlansError(ts("plansUnavailable"));
    } else {
      setPlansError("");
    }
    return { catalog, plans: bundleRes.data?.plans || [], subscription: nextSubscription };
  }, [ts]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const loaded = await load();
        if (cancelled) return;
        const pending = sessionStorage.getItem(CHECKOUT_FLAG) === "1";
        const status = loaded.subscription?.status;
        if (pending && LIVE_SUBSCRIPTION.has(status)) {
          sessionStorage.removeItem(CHECKOUT_FLAG);
          setStep("connect");
        } else if (pending && status === "waiting") {
          setStep("confirming");
        } else if (pending || status === "waiting") {
          setStep("unpaid");
        }
        if (new URLSearchParams(window.location.search).get("card") === "updated") {
          setSearchParams({}, { replace: true });
          if (loaded.subscription?.scheduledChange?.effectiveAt) setStep("updated");
        }
      } catch (err) {
        if (!cancelled) setError(ts("loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [load, ts, setSearchParams]);

  useEffect(() => {
    if (loading || checkoutSeeded.current || !subscription) return;
    if (subscription.status !== "waiting") return;
    checkoutSeeded.current = true;
    setAgreed(true);
    const items = subscription.items || [];
    if (!items.length) return;
    const globalItem = items.find((item) => {
      const plan = plans.find((entry) => entry.code === item.planCode);
      return plan && isGlobalCatalogPlan(plan);
    });
    if (globalItem) {
      setMode("global");
      setTierCode(globalItem.planCode);
      return;
    }
    setMode("custom");
    const next = {};
    items.forEach((item) => {
      const app = apps.find((candidate) => planForApp(candidate, plans)?.code === item.planCode);
      if (app) next[appId(app)] = Number(item.quantity || 1);
    });
    setQuantities(next);
  }, [loading, subscription, plans, apps]);

  const managing = LIVE_SUBSCRIPTION.has(subscription?.status);
  const changesLocked = subscription?.status === "past_due" || subscription?.status === "suspended";
  const canEdit = canPerform && !changesLocked;
  const globals = useMemo(() => globalPlans(plans), [plans]);
  const currentGlobal = heldGlobalPlan(subscription, plans);
  const currentLaunch = heldLaunchPlan(subscription, plans);
  const launchTrialActive = Boolean(
    subscription?.launch?.inTrial
    || (subscription?.trialEndsAt && new Date(subscription.trialEndsAt).getTime() > Date.now()),
  );
  const planCoversServices = Boolean(currentGlobal || currentLaunch);
  const renewalLabel = formatDate(
    currentLaunch?.code ? (subscription?.trialEndsAt || renewsAt || subscription?.renewsAt || subscription?.currentPeriodEnd) : (renewsAt || subscription?.renewsAt || subscription?.currentPeriodEnd),
    i18n.language
  );
  const tierScheduleLabel = useMemo(() => formatPeriodStartLabel({
    subscription,
    renewsAt,
    managing,
    useTrialEnd: Boolean(currentLaunch?.code),
    locale: i18n.language,
  }), [subscription, renewsAt, managing, currentLaunch?.code, i18n.language]);
  const launchCoverageLabel = formatDate(
    subscription?.launch?.coverageEndsAt || subscription?.trialEndsAt || subscription?.currentPeriodEnd || subscription?.renewsAt || renewsAt,
    i18n.language,
  ) || renewalLabel;

  const displayName = useCallback((app) => (
    (i18n.language === "ar" && app?.name_ar)
    || getLocalizedPlatformName(app?.name || app?.application || app?.appType, t)
    || app?.name
    || ""
  ), [i18n.language, t]);

  const heldQty = useCallback((app) => {
    if (!managing) return 0;
    if (currentGlobal) return resolvedCoveringBranchLimit(subscription, currentGlobal);
    if (currentLaunch) {
      return resolvedCoveringBranchLimit(subscription, currentLaunch) || Number(currentLaunch.branchLimit || 0);
    }
    const entitled = entitlementQtyForApp(app, subscription);
    if (entitled > 0) return entitled;
    const plan = planForApp(app, plans);
    const item = (subscription?.items || []).find((row) => row.planCode === plan?.code || row.featureKey === featureKeyForApp(app));
    return Number(item?.quantity || 0);
  }, [managing, currentLaunch, currentGlobal, plans, subscription]);

  const launchScheduledByApp = useMemo(() => {
    if (!currentLaunch || !subscription?.scheduledChange?.effectiveAt) return new Map();
    const map = new Map();
    const assignedPlans = new Set();
    (subscription.scheduledChange.items || [])
      .filter((item) => !isLaunchPlan({ code: item.planCode, name: item.planName }))
      .forEach((item) => {
        const code = item.planCode;
        if (!code || assignedPlans.has(code)) return;
        const app = apps.find((candidate) => planForApp(candidate, plans)?.code === code);
        if (!app) return;
        assignedPlans.add(code);
        map.set(appId(app), Number(item.quantity || 0));
      });
    return map;
  }, [currentLaunch, subscription, apps, plans]);

  const launchHandoffScheduled = launchScheduledByApp.size > 0;
  const launchSuccessorScheduled = Boolean(
    currentLaunch
    && subscription?.scheduledChange?.effectiveAt
    && (subscription.scheduledChange.items || []).some(
      (item) => !isLaunchPlan({ code: item.planCode, name: item.planName }),
    ),
  );
  const launchPicking = Boolean(currentLaunch && !launchHandoffScheduled);

  const pendingUpdatedSummary = useMemo(() => {
    const effectiveAt = subscription?.scheduledChange?.effectiveAt;
    if (!effectiveAt) return null;
    const date = formatDate(effectiveAt, i18n.language);
    const scheduledItems = (subscription.scheduledChange.items || [])
      .filter((item) => !isLaunchPlan({ code: item.planCode, name: item.planName }));
    if (!scheduledItems.length) return null;

    if (currentLaunch && launchHandoffScheduled) {
      const names = [];
      launchScheduledByApp.forEach((qty, id) => {
        if (qty <= 0) return;
        const app = apps.find((entry) => appId(entry) === id);
        if (app) names.push(displayName(app));
      });
      return { date, count: names.length, names: names.join(", "), launchHandoff: true };
    }

    const scheduledByCode = new Map(
      scheduledItems.map((item) => [item.planCode, Number(item.quantity || 0)]),
    );
    const globalScheduled = scheduledItems.find((item) => {
      const plan = plans.find((entry) => entry.code === item.planCode);
      return plan && isGlobalCatalogPlan(plan);
    });
    if (globalScheduled && currentGlobal) {
      return {
        date,
        count: 1,
        names: globalScheduled.planName || globalScheduled.planCode,
        launchHandoff: false,
      };
    }

    const names = [];
    apps.forEach((app) => {
      const plan = planForApp(app, plans);
      if (!plan || isGlobalCatalogPlan(plan) || isLaunchPlan(plan)) return;
      const nowQty = heldQty(app);
      const futureQty = scheduledByCode.get(plan.code) ?? 0;
      if (nowQty > futureQty) names.push(displayName(app));
    });

    if (!names.length) {
      const fallback = scheduledItems
        .filter((item) => Number(item.quantity || 0) > 0)
        .map((item) => item.planName || item.planCode);
      if (fallback.length) {
        return { date, count: fallback.length, names: fallback.join(", "), launchHandoff: false };
      }
      return null;
    }

    return { date, count: names.length, names: names.join(", "), launchHandoff: false };
  }, [
    subscription,
    apps,
    plans,
    currentLaunch,
    currentGlobal,
    launchHandoffScheduled,
    launchScheduledByApp,
    heldQty,
    displayName,
    i18n.language,
  ]);

  const scheduledGlobalPlanChange = useMemo(() => {
    if (!currentGlobal || !subscription?.scheduledChange?.effectiveAt) return null;
    const item = (subscription.scheduledChange.items || []).find((row) => {
      const plan = plans.find((entry) => entry.code === row.planCode);
      return plan && isGlobalCatalogPlan(plan);
    });
    if (!item) return null;
    const plan = plans.find((entry) => entry.code === item.planCode);
    if (!plan || plan.code === currentGlobal.code) return null;
    return { plan, effectiveAt: subscription.scheduledChange.effectiveAt };
  }, [currentGlobal, subscription, plans]);

  const pendingDisconnectBranchNames = useMemo(() => {
    const names = [];
    connections.forEach((row) => {
      if (!row.disconnectAtRenewal) return;
      const label = (i18n.language === "ar" && row.nameAr) || row.name;
      if (label && !names.includes(label)) names.push(label);
    });
    return names;
  }, [connections, i18n.language]);

  useEffect(() => {
    if (!launchHandoffScheduled || !managing) return;
    const scheduled = {};
    launchScheduledByApp.forEach((qty, id) => {
      scheduled[id] = qty;
    });
    setBaseline(scheduled);
    setQuantities((prev) => {
      if (!Object.keys(prev).length) {
        return { ...scheduled };
      }
      const ids = new Set([...Object.keys(scheduled), ...Object.keys(prev)]);
      for (const id of ids) {
        const cur = Number(prev[id] || 0);
        const base = Number(scheduled[id] || 0);
        if (cur !== base) return prev;
      }
      return { ...scheduled };
    });
  }, [launchHandoffScheduled, managing, launchScheduledByApp]);

  useEffect(() => {
    if (loading || !managing || seeded.current || currentGlobal) return;
    if (currentLaunch && !subscription) return;
    if (currentLaunch && launchHandoffScheduled) {
      seeded.current = true;
      return;
    }
    if (currentLaunch && launchPicking) {
      seeded.current = true;
      setQuantities({});
      setBaseline({});
      return;
    }
    const next = {};
    apps.forEach((app) => {
      if (currentLaunch) {
        const entitled = entitlementQtyForApp(app, subscription);
        const connected = connectedBranchCount(app, connections);
        const qty = entitled > 0 ? entitled : (connected > 0 ? Math.max(1, connected) : 0);
        if (qty > 0) next[appId(app)] = qty;
      } else {
        const qty = heldQty(app);
        if (qty > 0) next[appId(app)] = qty;
      }
    });
    if (!Object.keys(next).length && currentLaunch) {
      const entLoaded = subscription && typeof subscription.entitlements === "object";
      if (!entLoaded) return;
    }
    seeded.current = true;
    setQuantities(next);
    setBaseline(next);
  }, [loading, managing, apps, heldQty, currentGlobal, currentLaunch, launchPicking, launchHandoffScheduled, launchScheduledByApp, subscription, connections]);

  useEffect(() => {
    if (loading || stagedFromQuery.current) return;
    const coupon = searchParams.get("coupon");
    if (coupon) setCouponCode(coupon);
    const stage = searchParams.get("stage");
    const addBranch = searchParams.get("addBranch");
    const target = stage || addBranch;
    if (!target && !coupon) return;
    stagedFromQuery.current = true;
    const app = target ? apps.find((item) => appId(item) === target) : null;
    const held = app ? heldQty(app) : 0;
    const status = subscription?.status;
    if (stage && !addBranch && app && held > 0 && (status === "active" || status === "past_due")) {
      setSearchParams({}, { replace: true });
      navigate(connectPath(app));
      return;
    }
    if (!target) {
      setSearchParams({}, { replace: true });
      return;
    }
    setFocusId(target);
    if (managing && (!currentGlobal || currentLaunch)) {
      setQuantities((prev) => {
        const base = Number((seeded.current ? prev[target] : null) ?? baseline[target] ?? heldQty(apps.find((app) => appId(app) === target)) ?? 0);
        const nextQty = addBranch ? base + 1 : Math.max(base, 1);
        return { ...prev, [target]: nextQty };
      });
      setStep("choose");
    } else if (!managing) {
      setMode("custom");
      setTierCode(null);
      setQuantities((prev) => ({ ...prev, [target]: Math.max(1, Number(prev[target] || 0) || 1) }));
      setStep("choose");
    }
    setSearchParams({}, { replace: true });
  }, [loading, searchParams, managing, currentGlobal, baseline, heldQty, apps, setSearchParams, subscription, navigate]);

  const quantityFor = (app) => Number(quantities[appId(app)] || 0);
  const launchBranchMax = useCallback((app) => {
    const entitled = entitlementQtyForApp(app, subscription);
    if (entitled > 0) return entitled;
    return Number(currentLaunch?.branchLimit || 20);
  }, [subscription, currentLaunch]);
  const serviceRowVisible = useCallback((app) => {
    const id = appId(app);
    if (launchHandoffScheduled) {
      return launchScheduledByApp.has(id)
        || entitlementQtyForApp(app, subscription) > 0
        || connectedBranchCount(app, connections) > 0
        || quantityFor(app) > 0
        || Boolean(planForApp(app, plans));
    }
    if (launchPicking) {
      return entitlementQtyForApp(app, subscription) > 0
        || connectedBranchCount(app, connections) > 0
        || quantityFor(app) > 0
        || Boolean(planForApp(app, plans));
    }
    if (currentLaunch) return quantityFor(app) > 0 || heldQty(app) > 0;
    return heldQty(app) > 0 || quantityFor(app) > 0;
  }, [launchHandoffScheduled, launchPicking, currentLaunch, launchScheduledByApp, subscription, connections, plans, heldQty, quantities]);
  const selectedApps = useMemo(() => {
    const seen = new Set();
    return apps.filter((app) => {
      if (quantityFor(app) <= 0) return false;
      const id = appId(app);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [apps, quantities]);
  const launchHandoffPickDirty = launchPicking && selectedApps.length > 0
    && selectedApps.every((app) => Boolean(planForApp(app, plans)));
  const launchScheduleDirty = useMemo(() => {
    if (!launchHandoffScheduled) return false;
    const ids = new Set([...Object.keys(baseline), ...Object.keys(quantities)]);
    for (const id of ids) {
      const base = Number(baseline[id] || 0);
      const cur = Number(quantities[id] || 0);
      if (cur !== base) return true;
    }
    return false;
  }, [launchHandoffScheduled, baseline, quantities]);
  const launchPickQtyDirty = useMemo(() => {
    if (!launchPicking) return false;
    return selectedApps.some((app) => {
      const id = appId(app);
      return Number(quantities[id] || 0) !== Number(baseline[id] || 0);
    });
  }, [launchPicking, selectedApps, quantities, baseline]);
  const launchPickDirty = launchHandoffPickDirty || launchPickQtyDirty;
  const tierDirty = Boolean(managing && tierCode && !switchToCustom && (!currentGlobal || tierCode !== currentGlobal.code));
  const pendingTier = tierDirty ? globals.find((plan) => plan.code === tierCode) || null : null;
  const crossModelToCustomDirty = Boolean(
    managing && currentGlobal && switchToCustom && selectedApps.length > 0
    && selectedApps.every((app) => Boolean(planForApp(app, plans)))
  );
  const crossModelToGlobalDirty = Boolean(
    managing && !currentGlobal && tierDirty && pendingTier
  );
  const crossModelSwitch = crossModelToCustomDirty || crossModelToGlobalDirty;
  const modelSwitchTargetName = crossModelToGlobalDirty && pendingTier
    ? (pendingTier.name || ts("globalTitle"))
    : ts("modeCustom", { defaultValue: "Custom plan" });
  const modelSwitchNoticeText = useMemo(() => ts("modelSwitchNotice", {
    name: modelSwitchTargetName,
    date: formatDate(quote?.effective_at, i18n.language) || renewalLabel,
    defaultValue: MODEL_SWITCH_NOTICE_DEFAULT,
  }), [ts, modelSwitchTargetName, quote?.effective_at, renewalLabel, i18n.language]);
  const globalTierSwitch = Boolean(managing && currentGlobal && tierDirty && pendingTier && !crossModelSwitch);
  const dirty = managing && !currentGlobal && (
    launchPicking
      ? launchPickDirty
      : launchHandoffScheduled
        ? false
        : apps.some((app) => quantityFor(app) !== Number(baseline[appId(app)] || 0))
  );
  const jahezApp = apps.find((app) => /jahez/i.test(`${app?.name || ""} ${app?.name_ar || ""} ${app?.appType || ""}`));
  const showExample = Boolean(
    managing
    && jahezApp
    && Number(baseline[appId(jahezApp)] || heldQty(jahezApp) || 0) === 4
    && !dirty
    && !tierDirty
    && !subscription?.scheduledChange?.effectiveAt
  );
  const customFrom = useMemo(() => {
    const amounts = apps.map((app) => Number(planForApp(app, plans)?.amount)).filter((amount) => amount > 0);
    return amounts.length ? Math.min(...amounts) : null;
  }, [apps, plans]);
  const customInterval = useMemo(() => {
    const values = [...new Set(apps.map((app) => billingInterval(planForApp(app, plans))))];
    return values.length === 1 ? values[0] : "month";
  }, [apps, plans]);

  const quotedItems = useMemo(() => {
    const globalCodes = new Set(globals.map((plan) => plan.code));
    const singleGlobal = (code) => (code && globalCodes.has(code) ? [{ planCode: code, quantity: 1 }] : null);
    const finish = (lines) => {
      if (!lines?.length) return lines || [];
      const first = lines[0];
      if (first?.planCode && lines.length === 1 && globalCodes.has(first.planCode)) return lines;
      return mergeBundleItems(lines);
    };

    if (!managing && mode === "global" && tierCode) return [{ planCode: tierCode, quantity: 1 }];
    if (managing && currentGlobal && switchToCustom) {
      return finish(selectedApps
        .map((app) => {
          const plan = planForApp(app, plans);
          return plan ? { planCode: plan.code, quantity: quantityFor(app) } : null;
        })
        .filter(Boolean));
    }
    if (managing && pendingTier && tierDirty && !switchToCustom) {
      return [{ planCode: pendingTier.code, quantity: 1 }];
    }
    if (managing && currentGlobal && !switchToCustom) {
      const code = tierCode || currentGlobal.code;
      return [{ planCode: code, quantity: 1 }];
    }
    if (managing && tierCode) {
      const only = singleGlobal(tierCode);
      if (only) return only;
    }

    if (managing && currentLaunch && (launchHandoffScheduled || launchPicking)) {
      return finish(selectedApps
        .map((app) => {
          const plan = planForApp(app, plans);
          return plan ? { planCode: plan.code, quantity: quantityFor(app) } : null;
        })
        .filter(Boolean));
    }

    const bundle = selectedApps
      .map((app) => {
        const plan = planForApp(app, plans);
        return plan ? { planCode: plan.code, quantity: quantityFor(app) } : null;
      })
      .filter(Boolean);

    const globalLines = bundle.filter((item) => globalCodes.has(item.planCode));
    if (globalLines.length) {
      const code = tierCode
        || globalLines.sort((left, right) => (
          Number(globals.find((plan) => plan.code === right.planCode)?.amount || 0)
          - Number(globals.find((plan) => plan.code === left.planCode)?.amount || 0)
        ))[0]?.planCode;
      if (code) return [{ planCode: code, quantity: 1 }];
    }
    return finish(bundle);
  }, [managing, mode, tierCode, currentGlobal, currentLaunch, launchPicking, launchHandoffScheduled, launchScheduledByApp, pendingTier, globals, apps, plans, quantities, selectedApps, switchToCustom, tierDirty]);

  const checkoutItems = useMemo(() => {
    if (quotedItems.length) return quotedItems;
    if (subscription?.status !== "waiting") return [];
    const waitingItems = subscription.items || [];
    if (!waitingItems.length) return [];
    return mergeBundleItems(waitingItems.map((item) => ({
      planCode: item.planCode,
      quantity: Number(item.quantity || 1),
    })));
  }, [quotedItems, subscription]);

  const pricedPlans = useMemo(() => (
    quotedItems
      .map((item) => plans.find((plan) => plan.code === item.planCode))
      .filter(Boolean)
  ), [quotedItems, plans]);
  const quoteTrialDays = pricedPlans.reduce((max, plan) => Math.max(max, Number(plan.trialDays) || 0), 0);
  const checkoutTrialDays = useMemo(() => {
    if (quoteTrialDays > 0) return quoteTrialDays;
    return checkoutItems
      .map((item) => plans.find((plan) => plan.code === item.planCode))
      .filter(Boolean)
      .reduce((max, plan) => Math.max(max, Number(plan.trialDays) || 0), 0);
  }, [quoteTrialDays, checkoutItems, plans]);
  const quoteIntervals = [...new Set(pricedPlans.map((plan) => billingInterval(plan)))];
  const quoteInterval = quoteIntervals.length === 1 ? quoteIntervals[0] : "month";
  const hasPendingChanges = Boolean(
    dirty
    || tierDirty
    || crossModelSwitch
    || launchScheduleDirty
    || (launchPicking && launchPickDirty)
  );
  const quoteItemsKey = useMemo(() => (
    quotedItems
      .map((item) => `${item.planCode}:${item.quantity}`)
      .sort()
      .join("|")
  ), [quotedItems]);
  const quotePayloadItems = useMemo(() => quotedItems, [quoteItemsKey]);
  const shouldFetchQuote = useMemo(() => {
    if (!quotedItems.length) return false;
    if (step === "confirming" || step === "connect") return false;
    if (managing) return hasPendingChanges;
    return step === "choose" || step === "review";
  }, [quotedItems.length, step, managing, hasPendingChanges]);

  useEffect(() => {
    if (!shouldFetchQuote) {
      setQuote(null);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setQuoting(true);
      setQuoteError("");
      try {
        const res = await api.post("/subscriptions/bundle/quote", {
          items: quotePayloadItems,
          couponCode: couponCode || undefined,
          previewChange: managing,
          trialDays: quoteTrialDays,
        });
        if (!cancelled) setQuote(res.data || null);
      } catch (err) {
        if (!cancelled) {
          setQuote(null);
          setQuoteError(err.response?.data?.message || ts("quoteError"));
        }
      } finally {
        if (!cancelled) setQuoting(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [shouldFetchQuote, quoteItemsKey, couponCode, managing, quoteTrialDays, ts, quotePayloadItems]);

  useEffect(() => {
    if (step !== "confirming") return undefined;
    let tries = 0;
    let cancelled = false;
    const tick = async () => {
      tries += 1;
      try {
        const res = await api.get("/subscriptions/bundle");
        if (cancelled) return;
        const next = res.data?.subscription || null;
        setSubscription(next);
        setPlans(res.data?.plans || []);
        if (LIVE_SUBSCRIPTION.has(next?.status)) {
          sessionStorage.removeItem(CHECKOUT_FLAG);
          setStep("connect");
          return;
        }
      } catch (err) {
        if (!cancelled) setQuoteError(err.response?.data?.message || "");
      }
      if (tries >= 8 && !cancelled) setStep("unpaid");
    };
    const id = setInterval(tick, 2000);
    tick();
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [step]);

  const setQty = (app, raw) => {
    if (!canEdit) return;
    const id = appId(app);
    let next = Math.max(0, Number(raw) || 0);
    if (launchPicking || launchHandoffScheduled) {
      if (next > 0) {
        next = Math.min(launchBranchMax(app), Math.max(1, next));
      }
      setQuantities((prev) => ({ ...prev, [id]: next }));
    } else {
      setQuantities((prev) => {
        const copy = { ...prev };
        copy[id] = next;
        return copy;
      });
    }
    if (!managing) {
      setMode("custom");
      setTierCode(null);
    } else {
      setTierCode(null);
    }
  };

  const pickGlobalTier = useCallback((code) => {
    setTierCode(code);
    setMode("global");
    setQuantities({});
    setSwitchToCustom(false);
  }, []);

  const startSwitchToCustom = useCallback(() => {
    setSwitchToCustom(true);
    setTierCode(null);
    setPlanOpen(false);
    if (currentLaunch) {
      const scheduled = {};
      if (launchHandoffScheduled) {
        launchScheduledByApp.forEach((qty, id) => { scheduled[id] = qty; });
      }
      setQuantities(scheduled);
      setBaseline(scheduled);
      return;
    }
    const next = {};
    apps.forEach((app) => {
      const connected = connectedBranchCount(app, connections);
      if (connected > 0) next[appId(app)] = Math.min(connected, Number(currentGlobal?.branchLimit || connected));
    });
    setQuantities(next);
    setBaseline({});
  }, [apps, connections, currentGlobal, currentLaunch, launchHandoffScheduled, launchScheduledByApp]);

  const changes = useMemo(() => {
    const ups = [];
    const downs = [];
    if (!managing || currentGlobal || tierDirty) return { ups, downs };
    apps.forEach((app) => {
      const from = Number(baseline[appId(app)] || 0);
      const to = quantityFor(app);
      if (to > from) ups.push(app);
      else if (to < from) downs.push(app);
    });
    return { ups, downs };
  }, [managing, currentGlobal, tierDirty, apps, baseline, quantities]);

  const globalTierUpgrade = Boolean(
    globalTierSwitch
    && pendingTier
    && Number(pendingTier.amount || 0) > Number(currentGlobal?.amount || 0)
  );
  const globalTierDowngrade = Boolean(
    globalTierSwitch
    && pendingTier
    && Number(pendingTier.amount || 0) < Number(currentGlobal?.amount || 0)
  );
  const renewAtPeriodEnd = Boolean(
    managing
    && !currentLaunch
    && (
      crossModelSwitch
      || globalTierDowngrade
      || (changes.downs.length > 0 && changes.ups.length === 0 && !globalTierUpgrade)
    )
  );

  const flagKey = (row, id) => `${row.featureKey}:${id || row.branchId}`;
  const isFlagged = (row, id) => {
    const key = flagKey(row, id);
    if (Object.prototype.hasOwnProperty.call(flags, key)) return Boolean(flags[key]);
    return Boolean(row.disconnectAtRenewal);
  };

  const branchNeed = (app, targetOverride) => {
    const rows = connectionsForApp(app, connections).filter((row) => !row.paused);
    const target = targetOverride != null ? Number(targetOverride) : quantityFor(app);
    const chosenRows = rows.filter((row) => {
      const ids = row.branchIds?.length ? row.branchIds : [row.branchId];
      return ids.some((id) => isFlagged(row, id));
    });
    const required = Math.max(0, rows.length - target);
    return {
      rows,
      required,
      chosen: chosenRows.length,
      ready: chosenRows.length >= required,
      target,
    };
  };

  const launchHandoffApps = currentLaunch ? apps.filter((app) => quantityFor(app) > 0) : [];

  const startingTrialDays = !managing ? Number(quote?.trial_days ?? quoteTrialDays) || 0 : 0;
  const quoteDueToday = managing
    ? (quote?.due_today != null ? Number(quote.due_today) : null)
    : (startingTrialDays > 0 ? 0 : (quote?.amount != null ? Number(quote.amount) : null));
  const due = managing
    ? (renewAtPeriodEnd ? 0 : quoteDueToday)
    : (startingTrialDays > 0 ? 0 : (quote?.amount != null ? Number(quote.amount) : null));
  const tax = managing
    ? Number(quote?.due_today_tax || 0)
    : (startingTrialDays > 0
      ? 0
      : (quote?.tax_amount != null ? Number(quote.tax_amount) : (due != null ? Math.round(due * 15 / 115) : null)));
  const subtotal = due != null && tax != null ? Math.max(0, due - tax) : null;
  const monthly = quote?.amount != null ? Number(quote.amount) : null;
  const missingPlan = (!managing && mode === "custom" && selectedApps.some((app) => !planForApp(app, plans)))
    || (managing && !currentGlobal && [...changes.ups, ...changes.downs].some((app) => quantityFor(app) > 0 && !planForApp(app, plans)));
  const lastItemRemoval = managing && !currentGlobal && (subscription?.items || []).length > 0
    && changes.downs.length >= (subscription.items || []).length
    && changes.ups.length === 0
    && selectedApps.length === 0;

  const startCheckout = async () => {
    const resuming = step === "unpaid";
    const items = checkoutItems;
    if (!canPerform) return;
    if (!items.length) {
      toast.error(ts("checkoutLinkError", { defaultValue: "We could not find your plan selection. Change selection and try again." }));
      return;
    }
    if (!agreed && !resuming) return;
    if (missingPlan && !resuming) return;
    setBusy(true);
    try {
      sessionStorage.setItem(CHECKOUT_FLAG, "1");
      setStep("redirecting");
      const res = await api.post("/subscriptions/bundle/checkout", {
        items,
        couponCode: couponCode || undefined,
        trialDays: checkoutTrialDays,
        returnUrl: `${window.location.origin}/subscription`,
      });
      const checkoutUrl = res.data?.checkoutUrl || res.data?.checkout_url
        || res.data?.subscription?.checkoutUrl;
      if (res.data?.subscription) setSubscription(res.data.subscription);
      if (res.data?.routedFromCreate) {
        sessionStorage.removeItem(CHECKOUT_FLAG);
        setStep("updated");
        toast.info(ts("routedToSubscriptionUpdate", {
          defaultValue: "Your existing subscription was updated instead of creating a new one.",
        }));
        return;
      }
      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }
      if (LIVE_SUBSCRIPTION.has(res.data?.subscription?.status)) {
        sessionStorage.removeItem(CHECKOUT_FLAG);
        setStep("connect");
        return;
      }
      setStep("unpaid");
      toast.error(ts("checkoutLinkError", { defaultValue: "We could not open payment. Try again or change your selection." }));
    } catch (err) {
      sessionStorage.removeItem(CHECKOUT_FLAG);
      setStep(resuming ? "unpaid" : "review");
      const code = err.response?.data?.code;
      toast.error(
        code === "billing_profile_required" || code === "billing_profile_incomplete"
          ? ts("billingProfileRequired")
          : (err.response?.data?.message || ts("saveError"))
      );
    } finally {
      setBusy(false);
    }
  };

  const flushFlags = async () => {
    const jobs = [];
    connections.forEach((row) => {
      const ids = row.branchIds?.length ? row.branchIds : [row.branchId];
      ids.forEach((id) => {
        const key = flagKey(row, id);
        if (!Object.prototype.hasOwnProperty.call(flags, key)) return;
        const marked = Boolean(flags[key]);
        if (String(id) === String(row.branchId) && marked === Boolean(row.disconnectAtRenewal)) return;
        jobs.push(api.post("/subscriptions/bundle/disconnect", {
          branchId: id,
          appName: row.appName,
          marked,
        }));
      });
    });
    if (jobs.length) await Promise.all(jobs);
  };

  const syncLaunchScheduledQuantities = useCallback((sub) => {
    const next = {};
    const assignedPlans = new Set();
    (sub?.scheduledChange?.items || [])
      .filter((item) => !isLaunchPlan({ code: item.planCode, name: item.planName }))
      .forEach((item) => {
        const code = item.planCode;
        if (!code || assignedPlans.has(code)) return;
        const app = apps.find((candidate) => planForApp(candidate, plans)?.code === code);
        if (!app) return;
        assignedPlans.add(code);
        next[appId(app)] = Number(item.quantity || 0);
      });
    setBaseline(next);
    setQuantities(next);
  }, [apps, plans]);

  const confirmChanges = async () => {
    if (!canPerform || !flagsReady || missingPlan) return;
    if (subscription?.status === "past_due" || subscription?.status === "suspended") {
      toast.error(ts("payBeforeChange"));
      return;
    }
    setBusy(true);
    setDeclined(false);
    try {
      await flushFlags();
      if (launchScheduleDirty) {
        await api.delete("/subscriptions/bundle/scheduled-change");
      }
      const res = await api.post("/subscriptions/bundle/apply", { items: quotedItems });
      const nextSub = res.data?.subscription || null;
      setSubscription(nextSub);
      setFlags({});
      seeded.current = false;
      setSwitchToCustom(false);
      if (currentLaunch && nextSub?.scheduledChange?.effectiveAt) {
        syncLaunchScheduledQuantities(nextSub);
        seeded.current = true;
      }
      if (res.data?.cancelledModelSwitch?.label) {
        toast.info(ts("modelSwitchCancelled", { name: res.data.cancelledModelSwitch.label }));
      }
      const cardOnFile = Boolean(nextSub?.paymentMethod?.last4 || nextSub?.paymentMethod?.brand);
      if (currentLaunch && !cardOnFile) {
        try {
          const cardRes = await api.post("/subscriptions/bundle/payment-method", {
            returnUrl: `${window.location.origin}/subscription?card=updated`,
          });
          const url = cardRes.data?.url;
          if (url) {
            window.location.assign(url);
            return;
          }
        } catch (cardErr) {
          toast.error(cardErr.response?.data?.message || ts("saveError"));
          setStep("updated");
          return;
        }
      }
      setStep("updated");
      toast.success(ts("updatedTitle"));
    } catch (err) {
      const body = err.response?.data;
      if (body?.subscription) setSubscription(body.subscription);
      if (body?.code === "charge_failed" || err.response?.status === 422) {
        setDeclined(true);
        seeded.current = false;
      }
      toast.error(body?.message || ts("saveError"));
    } finally {
      setBusy(false);
    }
  };

  const requestConfirmChanges = () => {
    if (managing && crossModelSwitch) {
      setModelSwitchConfirmOpen(true);
      return;
    }
    confirmChanges();
  };

  const updateCard = async () => {
    if (!canPerform) return;
    setBusy(true);
    try {
      const res = await api.post("/subscriptions/bundle/payment-method", {
        returnUrl: `${window.location.origin}/subscription?card=updated`,
      });
      const url = res.data?.url;
      if (!url) {
        toast.error(ts("cardUpdateUnavailable"));
        return;
      }
      window.location.assign(url);
    } catch (err) {
      toast.error(err.response?.data?.message || ts("saveError"));
    } finally {
      setBusy(false);
    }
  };

  const undoPending = async () => {
    if (!canPerform) return;
    if (!undoAskOpen) {
      setUndoAskOpen(true);
      return;
    }
    setBusy(true);
    try {
      await api.delete("/subscriptions/bundle/scheduled-change");
      const refreshed = await api.get("/subscriptions/bundle");
      setSubscription(refreshed.data?.subscription || null);
      setQuantities({});
      seeded.current = false;
      setUndoAskOpen(false);
      setPendingExpanded(false);
      toast.success(ts("undoDone"));
    } catch (err) {
      toast.error(err.response?.data?.message || ts("saveError"));
    } finally {
      setBusy(false);
    }
  };

  const confirmCancel = async () => {
    if (!canCancel || busy) return;
    setBusy(true);
    try {
      const res = await api.post("/subscriptions/bundle/cancel", {});
      setSubscription(res.data?.subscription || null);
      setCancelOpen(false);
      toast.success(ts("canceled"));
    } catch (err) {
      toast.error(err.response?.data?.message || ts("saveError"));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!cancelOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !busy) setCancelOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cancelOpen, busy]);

  useEffect(() => {
    if (!modelSwitchConfirmOpen) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape" && !busy) setModelSwitchConfirmOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modelSwitchConfirmOpen, busy]);

  const steps = managing
    ? ["yourSubscription", "reviewChanges", "updatedStep"]
    : ["chooseServices", "review", "payment", "connectBranches"];
  const stepIndex = {
    choose: 0,
    review: 1,
    redirecting: 2,
    confirming: 2,
    unpaid: 2,
    connect: managing ? 0 : 3,
    updated: 2,
  }[step] ?? 0;

  const cardBrand = subscription?.paymentMethod?.brand || ts("card");
  const cardLabel = subscription?.paymentMethod?.last4
    ? `${cardBrand.charAt(0).toUpperCase()}${cardBrand.slice(1)} •••• ${subscription.paymentMethod.last4}`
    : "";
  const hasCard = Boolean(subscription?.paymentMethod?.last4 || subscription?.paymentMethod?.brand);
  const showUpdateCard = canPerform;

  const renderTile = (app) => {
    const id = appId(app);
    const name = displayName(app);
    const plan = planForApp(app, plans);
    const qty = quantityFor(app);
    const held = Number(baseline[id] || heldQty(app) || 0);
    const used = connectedBranchCount(app, connections);
    return (
      <article key={id} className={`sf-tile${qty > 0 ? " is-on" : ""}${focusId === id ? " is-flash" : ""}`}>
        <Logo app={app} name={name} />
        <div className="sf-name">{name}</div>
        <div className="sf-desc">{(i18n.language === "ar" && app.description_ar) || app.description}</div>
        <div className="sf-tile-foot">
          <div className="sf-price">
            {planCoversServices ? (
              <span className="sf-hint">{ts("allIncluded")}</span>
            ) : plan ? (
              <>
                <Money value={plan.amount} language={i18n.language} />
                <small> {ts(`perBranchInterval_${billingInterval(plan)}`)}</small>
              </>
            ) : "—"}
          </div>
          {managing && held > 0 ? (
            <button type="button" className="sf-btn link" onClick={() => navigate(connectPath(app))}>
              {ts("connectedOf", { used, total: held })}
            </button>
          ) : null}
          <div className="sf-act">
            {qty > 0 ? (
              <div className="sf-stepper">
                <button type="button" disabled={!canEdit || (launchHandoffScheduled && !launchScheduledByApp.has(appId(app)))} onClick={() => setQty(app, qty - 1)} aria-label={ts("fewer")}>−</button>
                <output>{ts("branchCount", { count: qty })}</output>
                <button type="button" disabled={!canEdit || (launchHandoffScheduled && !launchScheduledByApp.has(appId(app)))} onClick={() => setQty(app, qty + 1)} aria-label={ts("more")}>+</button>
              </div>
            ) : (
              <button type="button" className="sf-btn navy" disabled={!canEdit || !plan} onClick={() => setQty(app, 1)}>{ts("add")}</button>
            )}
          </div>
          {managing && held > qty ? <small className="sf-hint">{ts("keepUntil", { count: held, date: renewalLabel })}</small> : null}
        </div>
      </article>
    );
  };

  const renderCatalog = (onlyNew) => SECTIONS.map((section) => {
    const items = apps.filter((app) => {
      if (sectionOf(app) !== section.id) return false;
      if (!onlyNew) return true;
      if (currentLaunch) {
        if (launchHandoffScheduled) return false;
        return quantityFor(app) === 0;
      }
      return quantityFor(app) === 0 && heldQty(app) === 0;
    });
    if (!items.length) return null;
    const chosen = items.filter((app) => quantityFor(app) > 0).length;
    return (
      <section className="sf-section" key={section.id}>
        <div className="sf-section-head">
          <h2>
            {ts(section.title)}
            <span className={`sf-pill ${section.pillClass}`}>{ts(section.pill)}</span>
          </h2>
          <span className="sf-meta">
            {ts("addedCount", { chosen, total: items.length })}
            {!managing && chosen > 0 && canPerform ? (
              <>
                {" · "}
                <button
                  type="button"
                  className="sf-btn link"
                  onClick={() => {
                    setQuantities((prev) => {
                      const copy = { ...prev };
                      items.forEach((app) => delete copy[appId(app)]);
                      return copy;
                    });
                  }}
                >
                  {ts("clear")}
                </button>
              </>
            ) : null}
          </span>
        </div>
        <p className="sf-sub">{ts(section.hint)}</p>
        <div className="sf-tiles">{items.map(renderTile)}</div>
      </section>
    );
  });

  const tierCopy = (plan) => {
    const limit = String(plan.branchLimit || "");
    const translated = t(`subscriptionFlow.tierPerks.${limit}`, { returnObjects: true, defaultValue: [] });
    const perks = Array.isArray(translated) && translated.length
      ? [...translated]
      : [
        ts("allIncluded"),
        ts("upToBranches", { count: plan.branchLimit || "—" }),
      ].filter(Boolean);
    if (plan.trialDays > 0) perks.push(ts("trialDays", { count: plan.trialDays }));
    const blurb = t(`subscriptionFlow.tierBlurb.${limit}`, { defaultValue: plan.description || "" });
    return { perks, blurb: blurb.startsWith("subscriptionFlow.") ? (plan.description || "") : blurb };
  };

  const renderTiers = (selected, onPick, min = 0) => (
    <>
      <div className="sf-tiers">
        {globals.map((plan, index) => {
          const markedPopular = globals.some((item) => item.popular);
          const popular = markedPopular ? Boolean(plan.popular) : (globals.length >= 3 && index === 1);
          const interval = billingInterval(plan);
          const picked = selected === plan.code;
          const current = currentGlobal?.code === plan.code;
          const shortName = String(plan.name || "").replace(/^Global\s+/i, "");
          const tooSmall = min > 0 && Number(plan.branchLimit || 0) < min;
          const copy = tierCopy(plan);
          const upgrade = !currentLaunch && Number(plan.amount || 0) > Number(currentGlobal?.amount || 0);
          return (
            <article key={plan.code} className={`sf-tier${popular ? " is-popular" : ""}${picked ? " is-on" : ""}`}>
              {popular ? <span className="sf-ribbon">{ts("mostPopular")}</span> : null}
              <h3>{shortName}</h3>
              <div className="sf-tier-price">
                <Money value={plan.amount} language={i18n.language} />
                <small> {ts(`perInterval_${interval}`)}</small>
              </div>
              {copy.blurb ? <p className="sf-hint">{copy.blurb}</p> : null}
              <ul>
                {copy.perks.map((perk) => <li key={perk}>{perk}</li>)}
              </ul>
              <button type="button" className={`sf-btn wide${current ? "" : " navy"}`} disabled={!canEdit || current || tooSmall} onClick={() => onPick(plan.code)}>
                {current ? ts("currentPlan") : tooSmall ? ts("needsBranches", { count: min }) : picked ? ts("selected") : ts("selectPlan", { name: shortName })}
              </button>
              <small className="sf-hint">
                {!managing
                  ? (plan.trialDays > 0
                    ? ts(`trialFootnote_${interval}`, { count: plan.trialDays, amount: formatMinor(plan.amount, i18n.language) })
                    : ts(`perIntervalVat_${interval}`))
                  : current
                    ? ts("renewsOn", { date: tierScheduleLabel, defaultValue: "Renews on {{date}}" })
                    : upgrade
                      ? ts(`startsNowDiff_${interval}`)
                      : ts("startsOnRenewal", { date: tierScheduleLabel, defaultValue: "Starts on {{date}}" })}
              </small>
            </article>
          );
        })}
      </div>
      <p className="sf-hint sf-center-note">
        {ts("moreBranches")} <button type="button" className="sf-btn link" onClick={contactUs}>{ts("contactUs")}</button> {ts("forCustomPlan")}
      </p>
    </>
  );

  const subscribedApps = apps.filter((app) => heldQty(app) > 0);
  const serviceCount = subscribedApps.length;
  const coveringPlanMonthly = useMemo(() => {
    if (launchSuccessorScheduled) {
      const scheduled = (subscription?.scheduledChange?.items || [])
        .filter((item) => !isLaunchPlan({ code: item.planCode, name: item.planName }));
      const scheduledTotal = scheduled.reduce((sum, item) => {
        const plan = plans.find((entry) => entry.code === item.planCode);
        return sum + Number(plan?.amount || 0) * Number(item.quantity || 1);
      }, 0);
      if (scheduledTotal > 0) return scheduledTotal;
    }
    if (currentGlobal) return Number(currentGlobal.amount || 0);
    if (currentLaunch) {
      const catalog = plans.find((plan) => plan.code === currentLaunch.code);
      return Number(catalog?.amount ?? subscription?.nextCharge?.amount ?? 0);
    }
    return null;
  }, [currentGlobal, currentLaunch, launchSuccessorScheduled, subscription, plans]);
  const alacarteMonthly = useMemo(() => {
    if (currentGlobal || currentLaunch) return null;
    const fromDoc = (subscription?.items || []).reduce((sum, item) => {
      const plan = plans.find((entry) => entry.code === item.planCode);
      if (!plan || isGlobalCatalogPlan(plan) || isLaunchPlan(plan)) return sum;
      return sum + Number(plan.amount || 0) * Number(item.quantity || 1);
    }, 0);
    if (fromDoc > 0) return fromDoc;
    return apps.reduce((sum, app) => {
      const qty = quantityFor(app) > 0 ? quantityFor(app) : heldQty(app);
      if (qty <= 0) return sum;
      const plan = planForApp(app, plans);
      return sum + Number(plan?.amount || 0) * qty;
    }, 0);
  }, [currentGlobal, currentLaunch, subscription, plans, apps, quantities, heldQty]);
  const displayMonthlyTotal = coveringPlanMonthly != null ? coveringPlanMonthly : alacarteMonthly;
  const planPrice = coveringPlanMonthly;
  const serviceMonthly = alacarteMonthly ?? 0;
  const heldMonthly = displayMonthlyTotal ?? 0;
  const launchStagedMonthly = useMemo(() => {
    if (!currentLaunch || (!launchScheduleDirty && !launchPickDirty)) return null;
    return selectedApps.reduce((sum, app) => {
      const plan = planForApp(app, plans);
      if (!plan) return sum;
      return sum + Number(plan.amount || 0) * quantityFor(app);
    }, 0);
  }, [currentLaunch, launchScheduleDirty, launchPickDirty, selectedApps, plans, quantities]);
  const launchRowMonthlyTotal = useMemo(() => {
    if (!currentLaunch) return null;
    if (launchStagedMonthly != null) return launchStagedMonthly;
    const sumQtyRows = (qtyFor) => apps.reduce((sum, app) => {
      const plan = planForApp(app, plans);
      if (!plan) return sum;
      const qty = qtyFor(app);
      if (qty <= 0) return sum;
      return sum + Number(plan.amount || 0) * qty;
    }, 0);
    if (launchHandoffScheduled) {
      return sumQtyRows((app) => {
        const id = appId(app);
        return Number(quantities[id] ?? launchScheduledByApp.get(id) ?? 0);
      });
    }
    return sumQtyRows((app) => quantityFor(app));
  }, [
    currentLaunch,
    launchStagedMonthly,
    launchHandoffScheduled,
    apps,
    plans,
    quantities,
    launchScheduledByApp,
  ]);
  const activeServiceCounts = useMemo(() => {
    const tally = (resolveQty) => {
      let services = 0;
      let branches = 0;
      apps.forEach((app) => {
        if (currentLaunch && !planForApp(app, plans)) return;
        const id = appId(app);
        const qty = Number(resolveQty(app, id) || 0);
        if (qty <= 0) return;
        services += 1;
        branches += qty;
      });
      return { services, branches };
    };

    if (currentGlobal) return null;

    if (currentLaunch && launchHandoffScheduled) {
      return tally((app, id) => quantities[id] ?? launchScheduledByApp.get(id) ?? 0);
    }

    if (currentLaunch && launchPicking) {
      return tally((app, id) => quantityFor(app));
    }

    if (currentLaunch) return null;

    const editing = apps.some((app) => quantityFor(app) !== Number(baseline[appId(app)] || 0));
    return tally((app, id) => (editing ? quantityFor(app) : Number(baseline[id] || 0)));
  }, [
    currentGlobal,
    currentLaunch,
    launchHandoffScheduled,
    launchPicking,
    launchScheduledByApp,
    apps,
    plans,
    quantities,
    baseline,
  ]);
  const yourServicesPillCount = useMemo(() => {
    if (activeServiceCounts) return activeServiceCounts.services;
    if (currentLaunch) {
      return apps.filter((app) => Boolean(planForApp(app, plans))).length;
    }
    return apps.filter(serviceRowVisible).length;
  }, [activeServiceCounts, currentLaunch, apps, plans, serviceRowVisible]);
  const tierStartsNow = Boolean(globalTierUpgrade);
  const tierDowngradeReview = Boolean(globalTierDowngrade);
  const crossModelGlobalTarget = Boolean(crossModelSwitch && pendingTier);
  const crossModelBranchLimit = Number(pendingTier?.branchLimit || 0);
  const crossModelBranchApps = crossModelGlobalTarget
    ? apps.filter((app) => connectedBranchCount(app, connections) > crossModelBranchLimit)
    : [];
  const globalToCustomBranchApps = crossModelToCustomDirty ? selectedApps : [];
  const tierBranchLimit = Number(pendingTier?.branchLimit || 0);
  const tierDowngradeApps = tierDowngradeReview
    ? apps.filter((app) => connectedBranchCount(app, connections) > tierBranchLimit)
    : [];
  const flagsReady = (currentLaunch
    ? launchHandoffApps.every((app) => branchNeed(app).ready)
    : true) && changes.downs.every((app) => branchNeed(app).ready)
    && tierDowngradeApps.every((app) => branchNeed(app, tierBranchLimit).ready)
    && crossModelBranchApps.every((app) => branchNeed(app, crossModelBranchLimit).ready)
    && globalToCustomBranchApps.every((app) => branchNeed(app).ready);
  const globalNeed = (currentLaunch ? apps : subscribedApps).reduce((max, app) => (
    Math.max(max, connectedBranchCount(app, connections), currentLaunch ? 0 : heldQty(app))
  ), 0);
  const globalFit = globals.find((plan) => Number(plan.branchLimit || 0) >= globalNeed) || null;

  const disconnectShortfall = (currentLaunch ? launchHandoffApps : changes.downs)
    .map((app) => branchNeed(app))
    .concat(tierDowngradeApps.map((app) => branchNeed(app, tierBranchLimit)))
    .find((info) => info.required > 0 && !info.ready);

  const renderSummary = (action) => (
    <section className="sf-section sf-summary">
      <h2>{ts("summary")}</h2>
      <dl className="sf-totals">
        {managing ? (
          <>
            <div><dt>{ts("dueToday")}</dt><dd>{due != null ? <Money value={due} language={i18n.language} /> : "—"}</dd></div>
            <div><dt>{ts(`periodNow_${quoteInterval}`)}</dt><dd><Money value={heldMonthly} language={i18n.language} /></dd></div>
            <div className="grand"><dt>{ts(`periodFrom_${quoteInterval}`, { date: renewalLabel })}</dt><dd>{monthly != null ? <Money value={monthly} language={i18n.language} /> : "—"}</dd></div>
          </>
        ) : (
          <>
            <div><dt>{ts("subtotal")}</dt><dd>{subtotal != null ? <Money value={subtotal} language={i18n.language} /> : "—"}</dd></div>
            <div><dt>{ts("vat")}</dt><dd>{tax != null ? <Money value={tax} language={i18n.language} /> : "—"}</dd></div>
            <div className="grand"><dt>{ts("dueToday")}</dt><dd>{due != null ? <Money value={due} language={i18n.language} /> : "—"}</dd></div>
          </>
        )}
      </dl>
      {!managing ? (
        <p className="sf-hint">{ts(`renewsNote_${quoteInterval}`, { date: renewalLabel, amount: monthly != null ? formatMinor(monthly, i18n.language) : "—" })}</p>
      ) : due > 0 ? (
        <div className="sf-actions">
          <span>{cardLabel || ts("card")}</span>
          <button type="button" className="sf-btn link" onClick={updateCard}>{ts("updateCard")}</button>
        </div>
      ) : (
        <p className="sf-hint">{ts("nothingCharged")}</p>
      )}
      {quoteError ? <p className="sf-error">{quoteError}</p> : null}
      {missingPlan ? <p className="sf-error">{ts("noMatchingPlans")}</p> : null}
      {declined ? (
        <div className="sf-alert" role="alert">
          <b>{ts("cardDeclined")}</b>
          <div className="sf-actions">
            <button type="button" className="sf-btn navy" onClick={updateCard}>{ts("updateCard")}</button>
            <button type="button" className="sf-btn" onClick={confirmChanges}>{ts("tryAgain")}</button>
          </div>
        </div>
      ) : null}
      {!managing ? (
        <label className="sf-check">
          <input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} />
          {ts("agree")}
        </label>
      ) : null}
      <label className="sf-field">
        <span>{ts("coupon")}</span>
        <input value={couponCode} onChange={(event) => setCouponCode(event.target.value)} placeholder={ts("coupon")} />
      </label>
      {lastItemRemoval ? (
        <p className="sf-hint">
          {ts("keepOneService")}
          {canCancel ? (
            <>
              {" "}
              <button type="button" className="sf-btn link" disabled={busy} onClick={() => setCancelOpen(true)}>{ts("cancelSubscription")}</button>
            </>
          ) : null}
        </p>
      ) : null}
      {currentLaunch && !quotedItems.length ? (
        <p className="sf-hint">{ts("launchPickOneService", { defaultValue: "Choose at least one service. Each starts when your free month ends." })}</p>
      ) : null}
      {!flagsReady && managing ? (
        <p className="sf-hint">
          {disconnectShortfall
            ? ts("chooseBranchesProgress", {
              connected: disconnectShortfall.rows.length,
              required: disconnectShortfall.required,
              chosen: disconnectShortfall.chosen,
            })
            : ts("chooseBranchesHint")}
        </p>
      ) : null}
      <div className="sf-summary-actions">
        {action}
        <button type="button" className="sf-btn" onClick={() => setStep("choose")}>{managing ? ts("back") : ts("backToServices")}</button>
      </div>
    </section>
  );

  const renderKeep = (app, targetOverride) => {
    const info = branchNeed(app, targetOverride);
    if (!(info.required > 0)) return null;
    return (
      <div className="sf-keep" key={appId(app)}>
        <p>
          <b>{ts("chooseKeepLead", {
            connected: ts("branchCount", { count: info.rows.length }),
            name: displayName(app),
            required: info.required,
            date: renewalLabel,
          })}</b>
          <span className="sf-hint">{ts("chooseKeepHint", {
            chosen: info.chosen,
            required: info.required,
            keep: ts("branchCount", { count: info.target }),
            date: renewalLabel,
          })}</span>
        </p>
        <div className="sf-choices">
          {info.rows.map((row) => {
            const ids = row.branchIds?.length ? row.branchIds : [row.branchId];
            const on = ids.some((id) => isFlagged(row, id));
            return (
              <label key={row.branchId} className={`sf-choice${on ? " is-on" : ""}`}>
                <input
                  type="checkbox"
                  checked={on}
                  disabled={!on && info.chosen >= info.required}
                  onChange={() => {
                    setFlags((prev) => {
                      const copy = { ...prev };
                      ids.forEach((id) => { copy[flagKey(row, id)] = !on; });
                      return copy;
                    });
                  }}
                />
                {(i18n.language === "ar" && row.nameAr) || row.name}
                {on ? ` · ${ts("disconnects")}` : ""}
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  let body = null;
  if (loading) {
    body = <div className="d-flex justify-content-center py-5"><MDBSpinner role="status" /></div>;
  } else if (step === "redirecting" || step === "confirming") {
    body = (
      <section className="sf-section sf-center">
        <div className="sf-spin" aria-hidden="true" />
        <h2>{step === "confirming" ? ts("confirming") : ts("redirecting")}</h2>
        <p className="sf-hint">{step === "confirming" ? ts("confirmingHint") : ts("redirectingHint")}</p>
      </section>
    );
  } else if (step === "unpaid") {
    body = (
      <section className="sf-section sf-center">
        <div className="sf-mark warn">!</div>
        <h2>{ts("notCompleted")}</h2>
        <p className="sf-hint">{ts("notCompletedHint")}</p>
        <div className="sf-actions" style={{ justifyContent: "center" }}>
          <button type="button" className="sf-btn" onClick={() => { sessionStorage.removeItem(CHECKOUT_FLAG); setStep("choose"); }}>{ts("changeSelection")}</button>
          <button type="button" className="sf-btn brand" onClick={startCheckout} disabled={busy || !canPerform}>{ts("resumePay")}</button>
        </div>
      </section>
    );
  } else if (step === "connect") {
    const covering = heldCoveringPlan(subscription, plans);
    const sharedCovering = covering && (isGlobalCatalogPlan(covering) || covering.launch);
    const connectApps = apps.filter((app) => featureKeyForApp(app) || planForApp(app, plans));
    const rows = sharedCovering
      ? connectApps.map((app) => ({
        app,
        qty: branchLimitForConnect(app, subscription, plans),
      }))
      : (subscription?.items || []).map((item) => {
        const plan = plans.find((entry) => entry.code === item.planCode) || { featureKey: item.featureKey, name: item.planName };
        const app = apps.find((entry) => featureKeyForApp(entry) && featureKeyForApp(entry) === (plan.featureKey || item.featureKey))
          || apps.find((entry) => planForApp(entry, plans)?.code === item.planCode);
        return app ? { app, qty: branchLimitForConnect(app, subscription, plans) || Number(item.quantity || 0) } : null;
      }).filter(Boolean);
    const uniqueRows = [];
    const seenApps = new Set();
    rows.forEach((row) => {
      const id = appId(row.app);
      if (seenApps.has(id)) return;
      seenApps.add(id);
      uniqueRows.push(row);
    });
    body = (
      <>
        <section className="sf-section">
          <div className="sf-done">
            <div className="sf-mark">✓</div>
            <h2>{ts("activeTitle")}</h2>
            <p className="sf-hint">{ts("activeHint")}</p>
          </div>
        </section>
        <section className="sf-section">
          <div className="sf-section-head">
            <h2>{ts("connectBranches")}</h2>
            <span className="sf-meta">{sharedCovering ? ts("globalConnectPoolHint", {
              defaultValue: "Each service shares your plan branch limit.",
              count: resolvedCoveringBranchLimit(subscription, covering),
            }) : ts("opensApps")}</span>
          </div>
          {uniqueRows.map(({ app, qty }) => {
            const used = connectedBranchCount(app, connections);
            const meterWidth = qty > 0 ? `${Math.min(100, Math.round((used / qty) * 100))}%` : "0%";
            return (
              <div className="sf-map sf-map--connect" key={appId(app)}>
                <span className="sf-svc"><Logo app={app} name={displayName(app)} /><b>{displayName(app)}</b></span>
                <div className="sf-usage-row sf-connect-usage">
                  <span className="aa-bm-label">
                    {ts("connectedOf", { used, total: qty })}
                  </span>
                  <span className="sf-meter aa-meter-sm" aria-hidden="true"><i style={{ width: meterWidth }} /></span>
                </div>
                <button type="button" className="sf-btn navy" onClick={() => navigate(connectPath(app))}>{ts("connectBranches")}</button>
              </div>
            );
          })}
          <div className="sf-actions">
            <button type="button" className="sf-btn" onClick={() => navigate("/apps-management")}>{ts("openApps")}</button>
          </div>
        </section>
      </>
    );
  } else if (step === "updated") {
    const scheduledItems = (subscription?.scheduledChange?.items || []).filter((item) => !isLaunchPlan({ code: item.planCode, name: item.planName }));
    const launchHandoff = Boolean(pendingUpdatedSummary?.launchHandoff);
    const handoffDate = pendingUpdatedSummary?.date || formatDate(subscription?.scheduledChange?.effectiveAt, i18n.language) || renewalLabel;
    const handoffLine = scheduledItems.map((item) => `${item.planName || item.planCode} × ${ts("branchCount", { count: item.quantity })}`).join(", ");
    const hasCard = Boolean(subscription?.paymentMethod?.last4 || subscription?.paymentMethod?.brand);
    const pendingDate = pendingUpdatedSummary?.date || renewalLabel;
    const showPendingBanner = Boolean(
      pendingUpdatedSummary
      && !pendingUpdatedSummary.launchHandoff
      && pendingUpdatedSummary.count > 0,
    );
    const goToPendingFromUpdated = () => {
      setPendingExpanded(true);
      seeded.current = false;
      setStep("choose");
    };
    body = (
      <section className="sf-section">
        <div className="sf-done">
          <div className="sf-mark">✓</div>
          <h2>{ts("updatedTitle")}</h2>
          <p className="sf-hint">
            {launchHandoff
              ? ts("launchStartsOn", {
                defaultValue: "{{items}} starts on {{date}}. You keep all services until then.",
                items: handoffLine,
                date: handoffDate,
              })
              : (subscription?.scheduledChange?.effectiveAt
                ? ts("updatedPendingExtended", {
                  defaultValue: "Everything keeps working until {{date}}. The branches below disconnect then; you can change them or undo until that day.",
                  date: pendingDate,
                })
                : ts("updatedNow"))}
          </p>
          {showPendingBanner ? (
            <div className="sf-pending sf-done-banner">
              <span>
                {ts("updatedChangesBanner", {
                  defaultValue: "{{count}} changes on {{date}} · {{names}}",
                  count: pendingUpdatedSummary.count,
                  date: pendingUpdatedSummary.date,
                  names: pendingUpdatedSummary.names,
                })}
              </span>
              <div className="sf-pending-actions">
                <button type="button" className="sf-btn link" onClick={goToPendingFromUpdated}>
                  {ts("updatedViewPending", { defaultValue: "View" })}
                </button>
              </div>
            </div>
          ) : null}
          {launchHandoff && !hasCard ? (
            <p className="sf-hint sf-done-banner">
              {ts("launchSaveCard", { defaultValue: "Save a card so the first charge can be taken on that date. Without a card, the account is suspended then. The check is 1 SAR and is refunded." })}
            </p>
          ) : null}
          <div className="sf-done-actions">
            {launchHandoff && !hasCard ? (
              <button type="button" className="sf-btn brand" disabled={busy || !canPerform} onClick={updateCard}>
                {ts("launchAddCard", { defaultValue: "Save card" })}
              </button>
            ) : null}
            <button type="button" className="sf-btn" onClick={() => navigate("/apps-management")}>{ts("seeInApps")}</button>
            <button type="button" className="sf-btn navy" onClick={() => { seeded.current = false; setStep("choose"); }}>
              {ts("backToSubscription")}
            </button>
          </div>
        </div>
      </section>
    );
  } else if (step === "review") {
    body = (
      <div className="sf-grid">
        <div>
          {crossModelSwitch && managing ? (
            <section className="sf-section">
              <div className="sf-banner" role="status">
                {modelSwitchNoticeText}
              </div>
            </section>
          ) : null}
          {!managing ? (
            <section className="sf-section">
              <table className="sf-table">
                <thead>
                  <tr>
                    <th>{ts("service")}</th>
                    <th className="r">{ts("branches")}</th>
                    <th className="r">{ts("perBranchCol")}</th>
                    <th className="r">{ts(`totalInterval_${quoteInterval}`)}</th>
                  </tr>
                </thead>
                <tbody>
                  {(mode === "global" && tierCode ? [globals.find((plan) => plan.code === tierCode)].filter(Boolean).map((plan) => (
                    <tr key={plan.code}>
                      <td>{plan.name}</td>
                      <td className="r">{plan.branchLimit || 1}</td>
                      <td className="r"><Money value={plan.amount} language={i18n.language} /></td>
                      <td className="r"><Money value={plan.amount} language={i18n.language} /></td>
                    </tr>
                  )) : selectedApps.map((app) => {
                    const plan = planForApp(app, plans);
                    const qty = quantityFor(app);
                    return (
                      <tr key={appId(app)}>
                        <td><span className="sf-svc"><Logo app={app} name={displayName(app)} /><span>{displayName(app)}<small>{ts(`kind_${sectionOf(app)}`)}</small></span></span></td>
                        <td className="r">{qty}</td>
                        <td className="r">{plan ? <Money value={plan.amount} language={i18n.language} /> : "—"}</td>
                        <td className="r">{plan ? <Money value={Number(plan.amount || 0) * qty} language={i18n.language} /> : "—"}</td>
                      </tr>
                    );
                  }))}
                </tbody>
              </table>
            </section>
          ) : currentLaunch ? (
            <section className="sf-section">
              <div className="sf-section-head">
                <h2>{ts("fromRenewal", { date: formatDate(quote?.effective_at, i18n.language) || renewalLabel })}</h2>
                <span className="sf-pill later">{ts("noChargeToday")}</span>
              </div>
              {pendingTier ? (
                <div className="sf-maprow">
                  <div className="sf-map">
                    <span className="sf-svc"><b>{pendingTier.name}</b></span>
                    <span>
                      {ts("upToBranches", { count: pendingTier.branchLimit || "—" })}
                      {" · "}
                      {ts("allServices")}
                      {pendingTier.amount != null ? <>{" · "}<Money value={pendingTier.amount} language={i18n.language} /> {ts("perMo")}</> : null}
                    </span>
                  </div>
                  <p className="sf-hint">
                    {ts("launchStartsOn", {
                      defaultValue: "{{items}} starts on {{date}}. You keep all services until then.",
                      items: pendingTier.name,
                      date: formatDate(quote?.effective_at, i18n.language) || renewalLabel,
                    })}
                  </p>
                  {crossModelBranchApps.length
                    ? crossModelBranchApps.map((app) => renderKeep(app, crossModelBranchLimit))
                    : (
                      <p className="sf-hint">{ts("withinLimitCount", {
                        connected: globalNeed,
                        count: pendingTier.branchLimit || "—",
                      })}</p>
                    )}
                </div>
              ) : (
                <>
                  <table className="sf-table">
                    <thead>
                      <tr>
                        <th>{ts("service")}</th>
                        <th className="r">{ts("branches")}</th>
                        <th className="r">{ts("perBranchCol")}</th>
                        <th className="r">{ts(`totalInterval_${quoteInterval}`)}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {launchHandoffApps.map((app) => {
                        const plan = planForApp(app, plans);
                        const qty = quantityFor(app);
                        return (
                          <tr key={appId(app)}>
                            <td>
                              <span className="sf-svc">
                                <Logo app={app} name={displayName(app)} />
                                <span>{displayName(app)}<small>{ts(`kind_${sectionOf(app)}`)}</small></span>
                              </span>
                            </td>
                            <td className="r">{qty}</td>
                            <td className="r">{plan ? <Money value={plan.amount} language={i18n.language} /> : "—"}</td>
                            <td className="r">{plan ? <Money value={Number(plan.amount || 0) * qty} language={i18n.language} /> : "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <p className="sf-hint">
                    {ts("launchStartsOn", {
                      defaultValue: "{{items}} starts on {{date}}. You keep all services until then.",
                      items: launchHandoffApps.map((app) => `${displayName(app)} × ${ts("branchCount", { count: quantityFor(app) })}`).join(", "),
                      date: formatDate(quote?.effective_at, i18n.language) || renewalLabel,
                    })}
                  </p>
                  {launchHandoffApps.map((app) => renderKeep(app))}
                </>
              )}
              {quote?.amount != null ? (
                <p className="sf-hint">
                  {ts("launchFirstCharge", {
                    defaultValue: "The first charge is {{amount}} on that date. Nothing is charged today.",
                    amount: formatMinor(quote.amount, i18n.language),
                  })}
                </p>
              ) : <p className="sf-hint">{ts("nothingCharged")}</p>}
            </section>
          ) : (
            <>
              {changes.ups.length && !renewAtPeriodEnd ? (
                <section className="sf-section">
                  <div className="sf-section-head"><h2>{ts("startsNow")}</h2><span className="sf-pill now">{ts("chargedToday")}</span></div>
                  <table className="sf-table">
                    <thead>
                      <tr>
                        <th>{ts("service")}</th>
                        <th className="r">{ts("branches")}</th>
                        <th className="r">{ts("periodCharge")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.ups.map((app) => {
                        const plan = planForApp(app, plans);
                        const line = (quote?.due_today_items || []).find((item) => (item.plan_code || item.planCode) === plan?.code);
                        const charge = line?.amount ?? line?.subtotal ?? line?.due;
                        return (
                          <tr key={appId(app)}>
                            <td>
                              <span className="sf-svc">
                                <Logo app={app} name={displayName(app)} />
                                <span>{displayName(app)}<small>{ts(`kind_${sectionOf(app)}`)}</small></span>
                              </span>
                            </td>
                            <td className="r"><QtyChange from={baseline[appId(app)] || 0} to={quantityFor(app)} /></td>
                            <td className="r">{charge != null ? <Money value={Number(charge)} language={i18n.language} /> : "—"}</td>
                          </tr>
                        );
                      })}
                      {tierStartsNow && pendingTier ? (
                        <tr key={pendingTier.code}>
                          <td>{pendingTier.name}</td>
                          <td className="r">{ts("upToBranches", { count: pendingTier.branchLimit || "—" })}</td>
                          <td className="r">{due != null ? <Money value={due} language={i18n.language} /> : "—"}</td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                </section>
              ) : null}
              {(changes.downs.length || (changes.ups.length && renewAtPeriodEnd) || crossModelToCustomDirty || crossModelToGlobalDirty || (pendingTier && !tierStartsNow && !crossModelSwitch)) ? (
                <section className="sf-section">
                  <div className="sf-section-head"><h2>{ts("fromRenewal", { date: renewalLabel })}</h2><span className="sf-pill later">{ts("noChargeToday")}</span></div>
                  {renewAtPeriodEnd ? changes.ups.map((app) => {
                    const fromQty = baseline[appId(app)] || 0;
                    const toQty = quantityFor(app);
                    return (
                      <div className="sf-maprow" key={appId(app)}>
                        <div className="sf-map">
                          <span className="sf-svc"><Logo app={app} name={displayName(app)} /><b>{displayName(app)}</b></span>
                          <span><QtyChange from={fromQty} to={toQty} /></span>
                        </div>
                      </div>
                    );
                  }) : null}
                  {changes.downs.map((app) => {
                    const info = branchNeed(app);
                    const fromQty = baseline[appId(app)] || 0;
                    const toQty = quantityFor(app);
                    return (
                      <div className="sf-maprow" key={appId(app)}>
                        <div className="sf-map">
                          <span className="sf-svc"><Logo app={app} name={displayName(app)} /><b>{displayName(app)}</b></span>
                          <span>{toQty === 0 ? ts("removed") : <QtyChange from={fromQty} to={toQty} />}</span>
                        </div>
                        {info.required > 0 ? (
                          <div className="sf-keep">
                            <p>
                              <b>{ts("chooseKeepLead", {
                                connected: ts("branchCount", { count: info.rows.length }),
                                name: displayName(app),
                                required: info.required,
                                date: renewalLabel,
                              })}</b>
                              <span className="sf-hint">{ts("chooseKeepHint", {
                                chosen: info.chosen,
                                required: info.required,
                                keep: ts("branchCount", { count: toQty }),
                                date: renewalLabel,
                              })}</span>
                            </p>
                            <div className="sf-choices">
                              {info.rows.map((row) => {
                                const ids = row.branchIds?.length ? row.branchIds : [row.branchId];
                                const on = ids.some((id) => isFlagged(row, id));
                                return (
                                  <label key={row.branchId} className={`sf-choice${on ? " is-on" : ""}`}>
                                    <input
                                      type="checkbox"
                                      checked={on}
                                      disabled={!on && info.chosen >= info.required}
                                      onChange={() => {
                                        setFlags((prev) => {
                                          const copy = { ...prev };
                                          ids.forEach((id) => { copy[flagKey(row, id)] = !on; });
                                          return copy;
                                        });
                                      }}
                                    />
                                    {(i18n.language === "ar" && row.nameAr) || row.name}
                                    {on ? ` · ${ts("disconnects")}` : ""}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ) : <p className="sf-hint">{ts("withinLimitCount", { connected: info.rows.length, count: toQty })}</p>}
                      </div>
                    );
                  })}
                  {crossModelToCustomDirty ? selectedApps.map((app) => {
                    const info = branchNeed(app);
                    const fromQty = Number(currentGlobal?.branchLimit || 0);
                    const toQty = quantityFor(app);
                    return (
                      <div className="sf-maprow" key={appId(app)}>
                        <div className="sf-map">
                          <span className="sf-svc"><Logo app={app} name={displayName(app)} /><b>{displayName(app)}</b></span>
                          <span><QtyChange from={fromQty} to={toQty} /></span>
                        </div>
                        {info.required > 0 ? renderKeep(app) : null}
                      </div>
                    );
                  }) : null}
                  {crossModelToGlobalDirty && pendingTier ? (
                    <div className="sf-maprow">
                      <div className="sf-map">
                        <span className="sf-svc"><b>{pendingTier.name}</b></span>
                        <span>{ts("upToBranches", { count: pendingTier.branchLimit || "—" })} · {ts("allServices")}</span>
                      </div>
                      {crossModelBranchApps.length
                        ? crossModelBranchApps.map((app) => renderKeep(app, crossModelBranchLimit))
                        : (
                          <p className="sf-hint">{ts("withinLimitCount", {
                            connected: globalNeed,
                            count: pendingTier.branchLimit || "—",
                          })}</p>
                        )}
                    </div>
                  ) : null}
                  {pendingTier && !tierStartsNow && !crossModelSwitch ? (
                    <div className="sf-maprow">
                      <div className="sf-map">
                        <span className="sf-svc"><b>{pendingTier.name}</b></span>
                        <span>{ts("upToBranches", { count: pendingTier.branchLimit || "—" })} · {ts("allServices")}</span>
                      </div>
                      {tierDowngradeApps.length ? (
                        tierDowngradeApps.map((app) => renderKeep(app, tierBranchLimit))
                      ) : (
                        <p className="sf-hint">{ts("withinLimitCount", {
                          connected: globalNeed,
                          count: pendingTier.branchLimit || "—",
                        })}</p>
                      )}
                    </div>
                  ) : null}
                </section>
              ) : null}
            </>
          )}
        </div>
        {renderSummary(!managing ? (
          <button type="button" className="sf-btn brand" disabled={busy || !canPerform || !agreed || quoting || !quote || missingPlan} onClick={startCheckout}>
            {busy ? ts("working") : ts("continuePay")}
          </button>
        ) : (
          <button type="button" className="sf-btn brand" disabled={busy || !canEdit || !flagsReady || missingPlan || lastItemRemoval || (!dirty && !tierDirty && !crossModelSwitch && !launchPickDirty && !launchScheduleDirty) || (currentLaunch && !quotedItems.length)} onClick={requestConfirmChanges}>
            {busy ? ts("working") : (!currentLaunch && due > 0 ? ts("confirmPay", { amount: formatMinor(due, i18n.language) }) : ts("confirmChanges"))}
          </button>
        ))}
      </div>
    );
  } else {
    const showBar = managing ? (dirty || tierDirty || crossModelSwitch || launchScheduleDirty || (launchPicking && launchPickDirty)) : (mode === "global" ? Boolean(tierCode) : selectedApps.length > 0);
    body = (
      <>
        {subscription?.status === "past_due" || subscription?.status === "suspended" ? (
          <div className="sf-banner" role="status">
            {subscription.status === "suspended" ? ts("suspendedBanner") : ts("pastDueBanner")}
            {subscription?.dunning?.next_retry_at ? ` ${ts("dunningRetry", { date: formatDate(subscription.dunning.next_retry_at, i18n.language) })}` : ""}
          </div>
        ) : null}
        {showExample ? (
          <div className="sf-demo-tip"><b>{ts("exampleToTry")}</b> {ts("exampleJahez")}</div>
        ) : null}
        {managing && crossModelSwitch ? (
          <div className="sf-banner" role="status">
            {modelSwitchNoticeText}
          </div>
        ) : null}
        {managing ? (
          <section className="sf-section">
            <div className="sf-section-head">
              <h2>{ts("yourPlan")} <span className="sf-pill teal">{ts(`status_${subscription.status}`, { defaultValue: subscription.status })}</span></h2>
              {renewalLabel ? (
                <span className="sf-meta">
                  {currentLaunch && launchTrialActive
                    ? ts("launchFreeUntil", { date: renewalLabel, defaultValue: "Free until {{date}}" })
                    : (currentLaunch
                      ? ts("launchActiveUntil", { date: launchCoverageLabel, defaultValue: "Launch active until {{date}}" })
                      : ts("renews", { date: renewalLabel }))}
                </span>
              ) : null}
            </div>
            <div className="sf-stats">
              <div className="sf-stat"><span>{ts("monthlyTotal")}</span><b>{(() => {
                if (currentLaunch && (launchScheduleDirty || launchPickDirty) && launchRowMonthlyTotal != null) {
                  return <Money value={launchRowMonthlyTotal} language={i18n.language} />;
                }
                if ((dirty || tierDirty || launchScheduleDirty || launchPickDirty) && monthly != null) {
                  return <Money value={monthly} language={i18n.language} />;
                }
                if (!dirty && !tierDirty && !launchScheduleDirty && !launchPickDirty && displayMonthlyTotal != null) {
                  return <Money value={displayMonthlyTotal} language={i18n.language} />;
                }
                if (displayMonthlyTotal != null) {
                  return <Money value={displayMonthlyTotal} language={i18n.language} />;
                }
                return monthly != null ? <Money value={monthly} language={i18n.language} /> : "—";
              })()}</b></div>
              <div className="sf-stat">
                <span>{currentLaunch ? ts("plan") : (currentGlobal ? ts("plan") : ts("services"))}</span>
                <b>{currentLaunch
                  ? `${String(currentLaunch.name || "Launch").replace(/^Launch\s*/i, "") || "Launch"} · ${ts("upToBranches", { count: currentLaunch.branchLimit || 20 })}`
                  : (currentGlobal
                    ? `${currentGlobal.name} · ${ts("upToBranches", { count: currentGlobal.branchLimit || "—" })}`
                    : (activeServiceCounts
                      ? ts("servicesStat", activeServiceCounts)
                      : ts("servicesStat", {
                        services: Object.keys(baseline).length,
                        branches: Object.values(baseline).reduce((sum, qty) => sum + Number(qty || 0), 0),
                      })))}</b>
              </div>
              <div className="sf-stat">
                <span>{ts("nextCharge")}</span>
                <b>
                  {renewalLabel ? <>{renewalLabel} · </> : null}
                  {subscription?.nextCharge?.amount != null && displayMonthlyTotal == null
                    ? <Money value={subscription.nextCharge.amount} language={i18n.language} />
                    : (displayMonthlyTotal != null
                      ? <Money value={displayMonthlyTotal} language={i18n.language} />
                      : (monthly != null ? <Money value={monthly} language={i18n.language} /> : "—"))}
                </b>
              </div>
              <div className="sf-stat">
                <span>{ts("card")}</span>
                <b>{cardLabel || "—"}</b>
                {showUpdateCard ? (
                  <button type="button" className="sf-btn link" onClick={updateCard}>
                    {currentLaunch && !hasCard ? ts("launchAddCard", { defaultValue: "Save card" }) : ts("updateCard")}
                  </button>
                ) : null}
              </div>
            </div>
            {currentLaunch ? (
              <section className="sf-section sf-launch-card">
                <div className="sf-section-head">
                  <h2>
                    {ts("yourPlanName", { name: String(currentLaunch.name || "Launch").replace(/^Launch\s*/i, "") || "Launch" })}
                    <span className="sf-pill teal">{ts("allIncluded")}</span>
                  </h2>
                  <span className="sf-meta">
                    {launchTrialActive
                      ? ts("launchFreeUntil", { date: launchCoverageLabel, defaultValue: "Free until {{date}}" })
                      : ts("includedUntilLaunch", { date: launchCoverageLabel, defaultValue: "Included until {{date}}" })}
                  </span>
                </div>
                <p className="sf-hint">
                  {launchHandoffScheduled && activeServiceCounts
                    ? ts("launchActiveWithHandoff", {
                      defaultValue: "Every service stays on Launch until {{date}}. After that you keep {{services}} services on {{branches}} branches (see below).",
                      date: launchCoverageLabel,
                      services: activeServiceCounts.services,
                      branches: activeServiceCounts.branches,
                    })
                    : ts("launchCoverageHint", {
                      defaultValue: "All Loops services and integrations are included on up to {{count}} branches until {{date}}.",
                      count: currentLaunch.branchLimit || 20,
                      date: launchCoverageLabel,
                    })}
                </p>
                {launchHandoffScheduled && activeServiceCounts ? (
                  <p className="sf-hint sf-meta-strong">
                    {ts("launchAfterTrialStat", {
                      defaultValue: "After {{date}}: {{services}} services · {{branches}} branches · {{amount}}/mo",
                      date: launchCoverageLabel,
                      services: activeServiceCounts.services,
                      branches: activeServiceCounts.branches,
                      amount: formatMinor(launchRowMonthlyTotal ?? displayMonthlyTotal ?? 0, i18n.language),
                    })}
                  </p>
                ) : null}
                <div className="sf-row-end">
                  <span className="sf-hint">
                    {ts("globalIncludedHint", {
                      count: currentLaunch.branchLimit || 20,
                      used: apps.reduce((max, app) => Math.max(max, connectedBranchCount(app, connections)), 0),
                    })}
                  </span>
                  <div className="sf-row-actions">
                    <button
                      type="button"
                      className="sf-btn"
                      onClick={() => {
                        if (switchToCustom) {
                          setSwitchToCustom(false);
                          setTierCode(null);
                          if (launchHandoffScheduled) {
                            const scheduled = {};
                            launchScheduledByApp.forEach((qty, id) => { scheduled[id] = qty; });
                            setQuantities(scheduled);
                            setBaseline(scheduled);
                          } else {
                            setQuantities({});
                            setBaseline({});
                          }
                          setPlanOpen(true);
                          return;
                        }
                        setPlanOpen((open) => !open);
                      }}
                    >
                      {planOpen && !switchToCustom ? ts("hidePlans") : ts("changePlan")}
                    </button>
                    {!switchToCustom ? (
                      <button type="button" className="sf-btn navy" disabled={!canEdit} onClick={startSwitchToCustom}>
                        {ts("switchToCustom", { defaultValue: "Switch to custom plan" })}
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="sf-btn"
                        disabled={!canEdit}
                        onClick={() => {
                          setSwitchToCustom(false);
                          setTierCode(null);
                          if (launchHandoffScheduled) {
                            const scheduled = {};
                            launchScheduledByApp.forEach((qty, id) => { scheduled[id] = qty; });
                            setQuantities(scheduled);
                            setBaseline(scheduled);
                          } else {
                            setQuantities({});
                            setBaseline({});
                          }
                        }}
                      >
                        {ts("cancelSwitch", { defaultValue: "Cancel switch" })}
                      </button>
                    )}
                  </div>
                </div>
                {planOpen && !switchToCustom ? (
                  globals.length
                    ? renderTiers(tierCode, pickGlobalTier, globalNeed)
                    : <p>{ts("noGlobal")} <button type="button" className="sf-btn link" onClick={contactUs}>{ts("contactUs")}</button></p>
                ) : null}
              </section>
            ) : null}
            {subscription?.scheduledChange?.effectiveAt ? (
              <div className="sf-pending">
                {undoAskOpen ? (
                  <div className="sf-ask">
                    <p>{ts("undoAsk", {
                      items: (subscription.scheduledChange.items || [])
                        .map((item) => `${item.planName || item.planCode} × ${item.quantity}`)
                        .join(", "),
                      defaultValue: "Cancel the pending change ({{items}}) and keep the current quantities?",
                    })}</p>
                    <div className="sf-actions">
                      <button type="button" className="sf-btn" disabled={busy} onClick={() => setUndoAskOpen(false)}>
                        {ts("keepCurrentQuantities", { defaultValue: "Keep current quantities" })}
                      </button>
                      <button type="button" className="sf-btn navy" disabled={busy} onClick={undoPending}>
                        {ts("undoConfirm", { defaultValue: "Yes, cancel change" })}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span>
                      {currentLaunch ? ts("upcomingPlan", {
                        defaultValue: "Upcoming plan: {{items}} from {{date}}",
                        date: formatDate(subscription.scheduledChange.effectiveAt, i18n.language),
                        items: (() => {
                          const pendingItems = subscription.scheduledChange.items || [];
                          const visible = pendingExpanded ? pendingItems : pendingItems.slice(0, 3);
                          const line = visible.map((item) => `${item.planName || item.planCode} × ${ts("branchCount", { count: item.quantity })}`).join(", ");
                          return pendingItems.length > 3 && !pendingExpanded ? `${line} …` : line;
                        })(),
                      }) : (pendingUpdatedSummary && !pendingUpdatedSummary.launchHandoff && !pendingExpanded
                        ? ts("pendingChangesBanner", {
                          defaultValue: "{{count}} changes on {{date}} · {{names}}",
                          count: pendingUpdatedSummary.count,
                          date: pendingUpdatedSummary.date,
                          names: pendingUpdatedSummary.names,
                        })
                        : ts("pendingFrom", {
                          date: formatDate(subscription.scheduledChange.effectiveAt, i18n.language),
                          items: (() => {
                            const pendingItems = subscription.scheduledChange.items || [];
                            const visible = pendingExpanded ? pendingItems : pendingItems.slice(0, 3);
                            const line = visible.map((item) => `${item.planName || item.planCode} × ${item.quantity}`).join(", ");
                            return pendingItems.length > 3 && !pendingExpanded ? `${line} …` : line;
                          })(),
                        }))}
                    </span>
                    <div className="sf-pending-actions">
                      {!pendingExpanded ? (
                        <button type="button" className="sf-btn link" onClick={() => setPendingExpanded(true)}>
                          {ts("pendingViewMore", { defaultValue: "View" })}
                        </button>
                      ) : null}
                      {canPerform ? (
                        <button type="button" className="sf-btn link" disabled={busy} onClick={undoPending}>{ts("undo")}</button>
                      ) : null}
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </section>
        ) : (
          <>
            <div className="sf-modes">
              <button type="button" className={`sf-mode${mode === "global" ? " is-on" : ""}`} aria-pressed={mode === "global"} onClick={() => { setMode("global"); setQuantities({}); }}>
                <span className="sf-radio" aria-hidden="true" />
                <b>{ts("modeGlobal")} <span className="sf-pill teal">{ts("allIncluded")}</span></b>
                <span>{ts("modeGlobalHint")}</span>
                {globals[0] ? (
                  <span className="sf-plan-price">
                    {ts("from")} <Money value={globals[0].amount} language={i18n.language} />
                    <small>
                      {" "}
                      {globals[0].trialDays > 0
                        ? ts(`perIntervalTrial_${billingInterval(globals[0])}`, { count: globals[0].trialDays })
                        : ts(`perInterval_${billingInterval(globals[0])}`)}
                    </small>
                  </span>
                ) : <span>{ts("noGlobal")}</span>}
              </button>
              <button type="button" className={`sf-mode${mode === "custom" ? " is-on" : ""}`} aria-pressed={mode === "custom"} onClick={() => { setMode("custom"); setTierCode(null); }}>
                <span className="sf-radio" aria-hidden="true" />
                <b>{ts("modeCustom")}</b>
                <span>{ts("modeCustomHint")}</span>
                <span className="sf-plan-price">
                  {customFrom != null ? (
                    <>
                      {ts("from")} <Money value={customFrom} language={i18n.language} />
                      <small> {ts(`perBranchInterval_${customInterval}`)}</small>
                    </>
                  ) : ts("fromCustom")}
                </span>
              </button>
            </div>
            {mode === "global" ? (
              <section className="sf-section">
                <div className="sf-section-head"><h2>{ts("chooseGlobal")}</h2><span className="sf-meta">{ts("everyService")}</span></div>
                {globals.length ? renderTiers(tierCode, pickGlobalTier) : <p>{ts("noGlobal")} <button type="button" className="sf-btn link" onClick={contactUs}>{ts("contactUs")}</button></p>}
              </section>
            ) : renderCatalog(false)}
          </>
        )}
        {managing && currentGlobal ? (
          <>
            <section className="sf-section">
              <div className="sf-section-head">
                <h2>
                  {ts("yourPlanName", { name: String(currentGlobal.name || "").replace(/^Global\s+/i, "") })}
                  <span className="sf-pill teal">{ts("allIncluded")}</span>
                </h2>
                <span className="sf-meta"><Money value={currentGlobal.amount} language={i18n.language} /> {ts(`perInterval_${billingInterval(currentGlobal)}`)}</span>
              </div>
              <ul className="sf-perks">
                {tierCopy(currentGlobal).perks.map((perk) => <li key={perk}>{perk}</li>)}
              </ul>
              {scheduledGlobalPlanChange && !tierDirty ? (
                <div className="sf-pending">
                  <span>
                    {pendingDisconnectBranchNames.length
                      ? ts("globalTierChangingDisconnect", {
                        defaultValue: "Changing to {{name}} on {{date}} · disconnecting {{branches}}",
                        name: String(scheduledGlobalPlanChange.plan.name || "").replace(/^Global\s+/i, ""),
                        date: formatDate(scheduledGlobalPlanChange.effectiveAt, i18n.language),
                        branches: pendingDisconnectBranchNames.join(", "),
                      })
                      : ts("globalTierChanging", {
                        defaultValue: "Changing to {{name}} on {{date}}",
                        name: String(scheduledGlobalPlanChange.plan.name || "").replace(/^Global\s+/i, ""),
                        date: formatDate(scheduledGlobalPlanChange.effectiveAt, i18n.language),
                      })}
                  </span>
                  {canPerform ? (
                    <div className="sf-pending-actions">
                      <button type="button" className="sf-btn link" disabled={busy} onClick={undoPending}>{ts("undo")}</button>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="sf-row-end">
                <span className="sf-hint">
                  {ts("globalIncludedHint", {
                    count: currentGlobal.branchLimit || 0,
                    used: apps.reduce((max, app) => Math.max(max, connectedBranchCount(app, connections)), 0),
                  })}
                </span>
                <div className="sf-row-actions">
                  <button type="button" className="sf-btn" onClick={() => setPlanOpen((open) => !open)}>
                    {planOpen ? ts("hidePlans") : ts("changePlan")}
                  </button>
                  {!switchToCustom ? (
                    <button type="button" className="sf-btn navy" disabled={!canEdit} onClick={startSwitchToCustom}>
                      {ts("switchToCustom", { defaultValue: "Switch to custom plan" })}
                    </button>
                  ) : (
                    <button type="button" className="sf-btn" disabled={!canEdit} onClick={() => { setSwitchToCustom(false); setQuantities({}); }}>
                      {ts("cancelSwitch", { defaultValue: "Cancel switch" })}
                    </button>
                  )}
                </div>
              </div>
              {planOpen ? renderTiers(tierCode, pickGlobalTier) : null}
            </section>
            {switchToCustom ? (
              <section className="sf-section">
                <div className="sf-section-head">
                  <h2>{ts("buildCustomPlan", { defaultValue: "Build your custom plan" })}</h2>
                  <span className="sf-meta">{ts("startsOnRenewal", { date: renewalLabel })}</span>
                </div>
                <p className="sf-sub">{ts("modelSwitchToCustomHint", {
                  defaultValue: "Choose services and branch counts for after {{date}}. Your Global plan stays active until then.",
                  date: renewalLabel,
                })}</p>
                <div className="sf-rows">
                  {apps.map((app) => {
                    const id = appId(app);
                    const plan = planForApp(app, plans);
                    const rowQty = quantityFor(app);
                    const used = connectedBranchCount(app, connections);
                    return (
                      <div className={`sf-svc-row${focusId === id ? " is-flash" : ""}`} key={id}>
                        <div className="sf-who">
                          <Logo app={app} name={displayName(app)} compact />
                          <div>
                            <b>{displayName(app)}</b>
                            <small>{plan ? <> <Money value={plan.amount} language={i18n.language} /> {ts("perBranchShort")}</> : null}</small>
                          </div>
                        </div>
                        <div className="sf-qty">
                          {rowQty > 0 ? (
                            <div className="sf-stepper">
                              <button type="button" disabled={!canEdit} onClick={() => setQty(app, rowQty - 1)} aria-label={ts("fewer")}>−</button>
                              <output>{ts("branchCount", { count: rowQty })}</output>
                              <button type="button" disabled={!canEdit} onClick={() => setQty(app, rowQty + 1)} aria-label={ts("more")}>+</button>
                            </div>
                          ) : (
                            <button type="button" className="sf-btn navy" disabled={!canEdit || !plan} onClick={() => setQty(app, 1)}>{ts("add")}</button>
                          )}
                        </div>
                        <div>
                          <button type="button" className="sf-btn link" onClick={() => navigate(connectPath(app))}>
                            {ts("connectedShort", { used, total: rowQty || currentGlobal.branchLimit || 0 })}
                          </button>
                        </div>
                        <div className="sf-amt">
                          {plan && rowQty > 0 ? <Money value={Number(plan.amount || 0) * rowQty} language={i18n.language} /> : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : (
            <section className="sf-section">
              <div className="sf-section-head">
                <h2>{ts("includedServices")}</h2>
                <span className="sf-meta">{ts("serviceCount", { count: apps.length })}</span>
              </div>
              <div className="sf-incl">
                {apps.map((app) => (
                  <button type="button" className="sf-incl-item sf-btn" key={appId(app)} onClick={() => navigate(connectPath(app))}>
                    <Logo app={app} name={displayName(app)} compact />
                    <span>
                      <b>{displayName(app)}</b>
                      <small>{ts("connectedShort", { used: connectedBranchCount(app, connections), total: currentGlobal.branchLimit || 0 })}</small>
                    </span>
                  </button>
                ))}
              </div>
            </section>
            )}
          </>
        ) : null}
        {managing && !currentGlobal ? (
          <>
            {!currentLaunch ? (
            <section className="sf-section sf-offer">
              <div className="sf-section-head">
                <h2>{ts("globalTitle")} <span className="sf-pill teal">{ts("allIncluded")}</span></h2>
                <span className="sf-meta">{ts("everyService")}</span>
              </div>
              <p className="sf-sub">
                {ts("globalYouPay")} <Money value={heldMonthly} language={i18n.language} />{ts("globalPerMoFor", { count: serviceCount })}
                {globalFit ? (
                  <> {globalFit.name} {ts("globalCovers", { count: globalFit.branchLimit || 0 })} <Money value={globalFit.amount} language={i18n.language} />{ts("globalPerMo")}</>
                ) : null}
              </p>
              <div className="sf-row-end">
                <span className="sf-hint">
                  {globalNeed > 0 ? ts("globalBelow", { count: globalNeed }) : ""}
                </span>
                {globals.length ? (
                  <button type="button" className="sf-btn navy" onClick={() => setGlobalOpen((open) => !open)}>
                    {globalOpen ? ts("hidePlans") : ts("seeGlobalPlans")}
                  </button>
                ) : (
                  <button type="button" className="sf-btn navy" onClick={contactUs}>{ts("contactUs")}</button>
                )}
              </div>
              {globalOpen && globals.length ? renderTiers(tierCode, pickGlobalTier, globalNeed) : null}
            </section>
            ) : null}
            {currentLaunch && !switchToCustom ? (
              <section className="sf-section">
                <div className="sf-section-head">
                  <h2>{ts("includedServices")}</h2>
                  <span className="sf-meta">{ts("serviceCount", { count: apps.length })}</span>
                </div>
                <div className="sf-incl">
                  {apps.map((app) => (
                    <button type="button" className="sf-incl-item sf-btn" key={appId(app)} onClick={() => navigate(connectPath(app))}>
                      <Logo app={app} name={displayName(app)} compact />
                      <span>
                        <b>{displayName(app)}</b>
                        <small>{ts("connectedShort", { used: connectedBranchCount(app, connections), total: currentLaunch.branchLimit || 20 })}</small>
                      </span>
                    </button>
                  ))}
                </div>
              </section>
            ) : (
            <section className="sf-section">
              <div className="sf-section-head">
                <h2>
                  {currentLaunch && switchToCustom ? ts("buildCustomPlan", { defaultValue: "Build your custom plan" }) : (
                    <>
                      {ts("yourServices")}
                      {" "}
                      <span className="sf-pill">
                        {yourServicesPillCount}
                      </span>
                    </>
                  )}
                </h2>
                <span className="sf-meta sf-meta-strong">
                  {currentLaunch && switchToCustom
                    ? ts("startsOnRenewal", { date: renewalLabel })
                    : (
                      <>
                        <Money value={displayMonthlyTotal ?? 0} language={i18n.language} />
                        {" "}{ts("servicesMonthFrom", { date: renewalLabel })}
                      </>
                    )}
                </span>
              </div>
              {currentLaunch && switchToCustom ? (
                <p className="sf-sub">{ts("launchSwitchCustomHint", {
                  defaultValue: "Choose services and branch counts for after {{date}}. Your Launch plan stays active until then.",
                  date: renewalLabel,
                })}</p>
              ) : null}
              {currentLaunch && launchHandoffScheduled && !switchToCustom ? (
                <p className="sf-sub">{ts("launchEditUpcomingBranches", {
                  defaultValue: "Keep services and set branch counts for after {{date}}. The review bar shows the monthly total when you change anything.",
                  date: renewalLabel,
                })}</p>
              ) : null}
              <div className="sf-rows">
                {(currentLaunch
                  ? apps.filter((app) => Boolean(planForApp(app, plans)))
                  : apps.filter(serviceRowVisible)).map((app) => {
                  const id = appId(app);
                  const plan = planForApp(app, plans);
                  const launchServiceUi = launchPicking || launchHandoffScheduled;
                  const rowQty = quantityFor(app);
                  const held = Number(baseline[id] || heldQty(app) || 0);
                  const qtyTouched = Object.prototype.hasOwnProperty.call(quantities, id);
                  const committedHeld = held;
                  const removingRow = committedHeld > 0 && rowQty === 0 && qtyTouched;
                  const alacarteBranchQty = qtyTouched ? rowQty : held;
                  const used = connectedBranchCount(app, connections);
                  const kind = ts(`kind_${sectionOf(app)}`);
                  const stepperMax = launchServiceUi ? launchBranchMax(app) : null;
                  const showStepper = launchServiceUi
                    ? rowQty > 0
                    : removingRow
                      ? false
                      : (held > 0 || rowQty > 0 || qtyTouched);
                  const showKeepItRemoving = removingRow;
                  const showKeepMe = launchServiceUi && rowQty === 0 && !showKeepItRemoving;
                  const showAddService = !launchServiceUi && held === 0 && !qtyTouched && rowQty === 0;
                  const stepperQty = launchServiceUi ? rowQty : alacarteBranchQty;
                  const billQty = removingRow ? 0 : (launchServiceUi ? rowQty : alacarteBranchQty);
                  const keptAfterTrial = rowQty > 0 && launchServiceUi;
                  let tagClass = "";
                  let tagLabel = "";
                  if (removingRow) {
                    tagClass = "rm";
                    tagLabel = ts("removing");
                  } else if (launchServiceUi && rowQty === 0) {
                    tagLabel = launchHandoffScheduled
                      ? ts("launchExpiresOn", { defaultValue: "Expiring on {{date}}", date: renewalLabel })
                      : ts("includedUntilLaunch", {
                        defaultValue: "Included until {{date}}",
                        date: renewalLabel,
                      });
                  } else if (keptAfterTrial) {
                    tagClass = "add";
                    tagLabel = ts("launchPickedForAfter", {
                      defaultValue: "Starts {{date}}",
                      date: renewalLabel,
                    });
                  } else if (held > 0 || rowQty > 0) {
                    tagLabel = ts("subscribed");
                  } else if (rowQty > 0) {
                    tagClass = "add";
                    tagLabel = ts("newTag");
                  } else if (!launchServiceUi && held === 0 && rowQty === 0) {
                    tagLabel = "";
                  }
                  if (!removingRow && !launchServiceUi && held > 0) {
                    if (rowQty > held) {
                      tagClass = "add";
                      tagLabel = `+${rowQty - held}`;
                    } else if (rowQty === 0 && qtyTouched) {
                      tagClass = "rm";
                      tagLabel = ts("removing");
                    } else if (rowQty < held) {
                      tagClass = "less";
                      tagLabel = `−${held - rowQty}`;
                    } else {
                      tagLabel = ts("subscribed");
                    }
                  }
                  const rowConnectTotal = launchServiceUi
                    ? (removingRow ? committedHeld : (rowQty > 0 ? rowQty : launchBranchMax(app)))
                    : (billQty > 0 ? billQty : held);
                  return (
                    <div className={`sf-svc-row${focusId === id ? " is-flash" : ""}`} key={id}>
                      <div className="sf-who">
                        <Logo app={app} name={displayName(app)} compact />
                        <div>
                          <b>{displayName(app)}</b>
                          <small>
                            {kind}
                            {planCoversServices && !currentLaunch
                              ? <> · {ts("allIncluded")}</>
                              : (plan ? <> · <Money value={plan.amount} language={i18n.language} /> {ts("perBranchShort")}</> : null)}
                          </small>
                        </div>
                      </div>
                      <div>{tagLabel ? <span className={`sf-tag${tagClass ? ` ${tagClass}` : ""}`}>{tagLabel}</span> : null}</div>
                      <div className="sf-qty">
                        {showStepper ? (
                          <div className="sf-stepper">
                            <button type="button" disabled={!canEdit} onClick={() => setQty(app, stepperQty - 1)} aria-label={ts("fewer")}>−</button>
                            <output>{ts("branchCount", { count: stepperQty })}</output>
                            <button type="button" disabled={!canEdit || (stepperMax != null && stepperQty >= stepperMax)} onClick={() => setQty(app, stepperQty + 1)} aria-label={ts("more")}>+</button>
                          </div>
                        ) : showKeepItRemoving ? (
                          <button type="button" className="sf-btn" disabled={!canEdit} onClick={() => setQty(app, committedHeld)}>
                            {ts("keepIt")}
                          </button>
                        ) : showKeepMe ? (
                          <button type="button" className="sf-btn" disabled={!canEdit} onClick={() => setQty(app, 1)}>
                            {launchHandoffScheduled ? ts("keepMe", { defaultValue: "Keep me" }) : ts("keepIt")}
                          </button>
                        ) : showAddService ? (
                          <button type="button" className="sf-btn navy" disabled={!canEdit || !plan} onClick={() => setQty(app, 1)}>{ts("add")}</button>
                        ) : null}
                        {removingRow ? (
                          <small className="sf-hint">{ts("keepUntil", { count: committedHeld, date: renewalLabel })}</small>
                        ) : launchServiceUi && rowQty === 0 ? (
                          <small className="sf-hint">{launchHandoffScheduled
                            ? ts("launchExpiresOn", { defaultValue: "Expiring on {{date}}", date: renewalLabel })
                            : ts("includedUntilLaunch", { defaultValue: "Included until {{date}}", date: renewalLabel })}</small>
                        ) : null}
                        {!launchServiceUi && held > rowQty && !removingRow ? <small className="sf-hint">{ts("keepUntil", { count: held, date: renewalLabel })}</small> : null}
                      </div>
                      <div>
                        {rowConnectTotal ? (
                          <button type="button" className="sf-btn link" onClick={() => navigate(connectPath(app))}>
                            {ts("connectedShort", { used, total: rowConnectTotal })}
                          </button>
                        ) : (
                          <small className="sf-hint">{ts("connectAfter")}</small>
                        )}
                      </div>
                      <div className="sf-amt">
                        {planCoversServices && !currentLaunch ? (
                          <span className="sf-hint">{ts("allIncluded")}</span>
                        ) : plan && (billQty > 0 || removingRow) ? (
                          <>
                            <Money value={Number(plan.amount || 0) * billQty} language={i18n.language} />
                            <small> {ts("perMo")}</small>
                          </>
                        ) : currentLaunch ? (
                          <span className="sf-hint">—</span>
                        ) : "—"}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
            )}
            {!currentLaunch ? (
            <>
            <div>
              <h2 className="sf-add-title">{ts("addMore")}</h2>
              <p className="sf-lead">{ts("addMoreHint")}</p>
            </div>
            {renderCatalog(true)}
            </>
            ) : null}
          </>
        ) : null}
        {showBar ? (
          <div className="sf-bar">
            <div className="sf-bar-inner">
            <div className="sf-chips">
              {!managing && mode === "global" && tierCode ? (
                <span className="sf-chip">
                  {globals.find((plan) => plan.code === tierCode)?.name}
                  {" · "}
                  {ts("upToBranches", { count: globals.find((plan) => plan.code === tierCode)?.branchLimit || "—" })}
                  {" · "}
                  {ts("allServices")}
                </span>
              ) : null}
              {managing && crossModelSwitch && !pendingTier ? (
                <span className="sf-chip">
                  {ts("modeCustom", { defaultValue: "Custom plan" })}
                  {" · "}
                  {ts("fromRenewal", { date: renewalLabel })}
                </span>
              ) : null}
              {managing && !currentGlobal && !tierDirty && !launchHandoffScheduled && !launchPicking && !crossModelSwitch ? apps.filter((app) => quantityFor(app) !== Number(baseline[appId(app)] || 0)).map((app) => {
                const was = Number(baseline[appId(app)] || 0);
                const to = quantityFor(app);
                const detail = was && to
                  ? <QtyChange from={was} to={to} />
                  : to ? `× ${to}` : ts("removed");
                return <span className="sf-chip" key={appId(app)}>{to > was ? "+" : "−"} {displayName(app)} {detail}</span>;
              }) : null}
              {managing && launchHandoffScheduled && launchScheduleDirty ? apps.filter((app) => {
                const id = appId(app);
                const was = Number(baseline[id] || 0);
                const to = Number(quantities[id] || 0);
                return was !== to;
              }).map((app) => {
                const id = appId(app);
                const was = Number(baseline[id] || 0);
                const to = Number(quantities[id] || 0);
                const detail = was && to
                  ? <QtyChange from={was} to={to} />
                  : to ? `× ${to}` : ts("removed");
                return <span className="sf-chip" key={id}>{to > was ? "+" : "−"} {displayName(app)} {detail}</span>;
              }) : null}
              {managing && launchPicking && launchPickDirty ? selectedApps.map((app) => (
                <span className="sf-chip" key={appId(app)}>{displayName(app)} × {quantityFor(app)}</span>
              )) : null}
              {!managing && mode === "custom" ? selectedApps.map((app) => (
                <span className="sf-chip" key={appId(app)}>{displayName(app)} × {quantityFor(app)}</span>
              )) : null}
              {pendingTier ? (
                <span className="sf-chip">
                  {pendingTier.name}
                  {" · "}
                  {ts("upToBranches", { count: pendingTier.branchLimit || "—" })}
                  {" · "}
                  {ts("allServices")}
                </span>
              ) : null}
            </div>
            <div className="sf-sum">
              <small>
                {managing
                  ? ts(`periodFrom_${quoteInterval}`, { date: renewalLabel })
                  : quoteTrialDays > 0
                    ? ts(`perIntervalAfterTrial_${quoteInterval}`, { count: quoteTrialDays })
                    : ts(`perIntervalVat_${quoteInterval}`)}
              </small>
              <b>{monthly != null
                ? <Money value={monthly} language={i18n.language} />
                : (launchStagedMonthly != null
                  ? <Money value={launchStagedMonthly} language={i18n.language} />
                  : (quoting ? "…" : "—"))}</b>
            </div>
            <button
              type="button"
              className="sf-btn ghost"
              onClick={() => {
                if (launchScheduleDirty) {
                  setQuantities({ ...baseline });
                  return;
                }
                if (managing) {
                  setQuantities(baseline);
                  setTierCode(currentGlobal?.code || null);
                  setFlags({});
                  setSwitchToCustom(false);
                } else {
                  setQuantities({});
                  setTierCode(null);
                }
              }}
            >
              {managing ? ts("discard") : ts("clearAll")}
            </button>
            <button
              type="button"
              className="sf-btn brand"
              disabled={!canEdit || (managing ? (!dirty && !tierDirty && !crossModelSwitch && !launchScheduleDirty && !(launchPicking && launchPickDirty)) : mode === "global" ? !tierCode : !selectedApps.length)}
              onClick={() => {
                setStep("review");
              }}
            >
              {managing ? ts("reviewChanges") : ts("review")}
            </button>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  return (
    <div className="sf-page oh-page" ref={pageRef} dir={i18n.dir()} lang={i18n.language}>
      <header>
        <h1>{step === "review" ? (managing ? ts("reviewChanges") : ts("reviewTitle")) : ts("title")}</h1>
        <p className="sf-lead">
          {step === "review"
            ? (managing ? ts(`reviewLead_${quoteInterval}`, { date: renewalLabel }) : ts("reviewNewLead"))
            : (managing ? ts("subtitleManage") : ts("subtitleNew"))}
        </p>
        {!canPerform ? <p className="sf-hint">{ts("viewOnly")}</p> : null}
      </header>
      {error ? <p className="sf-error">{error}</p> : null}
      {plansError ? <p className="sf-error">{plansError}</p> : null}
      <div className="sf-steps" aria-label={ts("steps")}>
        {steps.map((label, index) => (
          <span key={label} className={`sf-step${index === stepIndex ? " is-on" : ""}`}>{index + 1}. {ts(label)}</span>
        ))}
      </div>
      {body}
      {cancelOpen ? (
        <div className="sf-dialog-back" onMouseDown={() => { if (!busy) setCancelOpen(false); }}>
          <div
            className="sf-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sf-cancel-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <h2 id="sf-cancel-title">{ts("cancelDialogTitle")}</h2>
            <p>{ts("cancelDialogBody", { date: renewalLabel || "—" })}</p>
            <ul>
              <li>{ts("cancelDialogKeepAccess")}</li>
              <li>{ts("cancelDialogNoRefund")}</li>
            </ul>
            <div className="sf-dialog-actions">
              <button type="button" className="sf-btn" disabled={busy} onClick={() => setCancelOpen(false)}>{ts("cancelDialogKeep")}</button>
              <button type="button" className="sf-btn danger" disabled={busy} onClick={confirmCancel}>
                {busy ? ts("working") : ts("cancelDialogConfirm")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {modelSwitchConfirmOpen ? (
        <div className="sf-dialog-back" onMouseDown={() => { if (!busy) setModelSwitchConfirmOpen(false); }}>
          <div
            className="sf-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="sf-model-switch-title"
            onMouseDown={(event) => event.stopPropagation()}
          >
            <h2 id="sf-model-switch-title">{ts("modelSwitchConfirmTitle", { defaultValue: "Confirm plan switch" })}</h2>
            <p>{modelSwitchNoticeText}</p>
            <div className="sf-dialog-actions">
              <button type="button" className="sf-btn" disabled={busy} onClick={() => setModelSwitchConfirmOpen(false)}>
                {ts("cancelSwitch", { defaultValue: "Cancel switch" })}
              </button>
              <button
                type="button"
                className="sf-btn brand"
                disabled={busy}
                onClick={() => {
                  setModelSwitchConfirmOpen(false);
                  confirmChanges();
                }}
              >
                {busy ? ts("working") : ts("modelSwitchConfirmConfirm", { defaultValue: "Schedule switch at renewal" })}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default SubscriptionPage;
